import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { TrendingUp, Package, Users, Mail, Plus, Check, Trash2, FileDown, X, ChevronDown, ChevronUp, Edit, MapPin } from 'lucide-react';
import ProfitCircleChart from '@/components/ProfitCircleChart';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [groupedInvestments, setGroupedInvestments] = useState([]);
  const [cancelledInvestments, setCancelledInvestments] = useState([]);
  const [profits, setProfits] = useState([]);
  const [groupedProfits, setGroupedProfits] = useState([]);
  const [interests, setInterests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedInvestments, setExpandedInvestments] = useState({});
  const [expandedYears, setExpandedYears] = useState({});
  const [profitForm, setProfitForm] = useState({
    project_id: '',
    month: '',
    amount: ''
  });
  const [allProfits, setAllProfits] = useState([]);
  const [expandedProfits, setExpandedProfits] = useState({});
  const [profitDistributions, setProfitDistributions] = useState({});
  const [loadingDistribution, setLoadingDistribution] = useState({});
  const [investmentGrouping, setInvestmentGrouping] = useState('none'); // 'none', 'user', 'project'
  const [adminActiveTab, setAdminActiveTab] = useState('projects'); // Active tab in admin panel
  const [highlightedInvestmentId, setHighlightedInvestmentId] = useState(null); // For highlighting investment row
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(null); // Filter for payment status: 'offen', 'teilweise_bezahlt', 'bezahlt', or null
  const [projectFilter, setProjectFilter] = useState(null); // Filter for project ID
  const [userBankAccounts, setUserBankAccounts] = useState([]);
  const [pendingBankAccounts, setPendingBankAccounts] = useState([]);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [bankAccountForm, setBankAccountForm] = useState({
    project_id: '',
    account_holder: '',
    iban: '',
    bic: '',
    bank_name: ''
  });
  const [showExportModal, setShowExportModal] = useState(false);
  const [showProfitEditModal, setShowProfitEditModal] = useState(false);
  const [editingProfit, setEditingProfit] = useState(null);
  const [editingInvestmentBank, setEditingInvestmentBank] = useState(null);
  const [bankRequestsSortBy, setBankRequestsSortBy] = useState('all'); // 'all', 'user' or 'project'
  const [expandedBankRequests, setExpandedBankRequests] = useState({}); // Track expanded groups
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printFilters, setPrintFilters] = useState({
    startDate: '',
    endDate: '',
    filterBy: 'project', // 'user' or 'project'
    selectedId: '' // user_id or project_id
  });
  const [investmentBankForm, setInvestmentBankForm] = useState({
    iban: '',
    bic: '',
    account_holder: '',
    bank_name: ''
  });
  const [showPartialPaymentDialog, setShowPartialPaymentDialog] = useState(false);
  const [partialPaymentData, setPartialPaymentData] = useState({ 
    investmentId: null, 
    currentPaidAmount: 0, 
    newPaymentAmount: 0, 
    totalAmount: 0 
  });
  const [profitEditForm, setProfitEditForm] = useState({
    amount: '',
    resend_notifications: true
  });
  const [exportConfig, setExportConfig] = useState({
    type: 'user', // 'user' or 'project'
    format: 'pdf', // only PDF now
    entity_id: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      // Different data fetching based on role
      if (user.role === 'user') {
        // Regular users see only their own data
        const [investmentsRes, groupedRes, cancelledRes, profitsRes, groupedProfitsRes, interestsRes, bankAccountsRes, projectsRes] = await Promise.all([
          axios.get(`${API}/investments`),
          axios.get(`${API}/investments/grouped`),
          axios.get(`${API}/investments/cancelled`),
          axios.get(`${API}/profits/user`),
          axios.get(`${API}/profits/user/by-project`),
          axios.get(`${API}/interest`),
          axios.get(`${API}/user-bank-accounts`),
          axios.get(`${API}/projects`)
        ]);
        
        setInvestments(investmentsRes.data);
        setGroupedInvestments(groupedRes.data);
        setCancelledInvestments(cancelledRes.data);
        setProfits(profitsRes.data);
        setGroupedProfits(groupedProfitsRes.data);
        setInterests(interestsRes.data);
        setUserBankAccounts(bankAccountsRes.data);
        setProjects(projectsRes.data);
      } else {
        // Admin/Finance/Content/SuperAdmin see all data
        const [investmentsRes, interestsRes, projectsRes, usersRes] = await Promise.all([
          axios.get(`${API}/investments`),
          axios.get(`${API}/interest`),
          axios.get(`${API}/projects`),
          (user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') ? axios.get(`${API}/users`) : Promise.resolve({ data: [] })
        ]);
        
        setInvestments(investmentsRes.data);
        setInterests(interestsRes.data);
        setProjects(projectsRes.data);
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveInterest = async (interestId) => {
    if (!window.confirm('Möchten Sie dieses Interesse wirklich genehmigen?\n\nDer User kann danach Anteile am Projekt erwerben.')) {
      return;
    }
    
    try {
      await axios.put(`${API}/interest/${interestId}/approve`);
      toast.success('Interesse genehmigt');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Genehmigen');
    }
  };

  const handleRejectInterest = async (interestId) => {
    if (!window.confirm('Möchten Sie dieses Interesse wirklich ablehnen?')) {
      return;
    }
    
    try {
      await axios.put(`${API}/interest/${interestId}/reject`);
      toast.success('Interesse abgelehnt');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Ablehnen');
    }
  };

  const handleUpdatePayment = async (investmentId, paymentStatus) => {
    // If changing to "teilweise_bezahlt", open dialog to enter NEW payment amount
    if (paymentStatus === 'teilweise_bezahlt') {
      const investment = investments.find(inv => inv.id === investmentId);
      if (investment) {
        setPartialPaymentData({
          investmentId,
          currentPaidAmount: investment.paid_amount || 0,  // Already paid
          newPaymentAmount: 0,  // New payment to add
          totalAmount: investment.total_amount || 0
        });
        setShowPartialPaymentDialog(true);
      }
      return;
    }
    
    try {
      const paymentDate = paymentStatus === 'bezahlt' ? new Date().toISOString() : null;
      const payload = {
        payment_status: paymentStatus,
        payment_date: paymentDate
      };
      
      // If marking as "bezahlt", set paid_amount to full amount
      if (paymentStatus === 'bezahlt') {
        const investment = investments.find(inv => inv.id === investmentId);
        if (investment) {
          payload.paid_amount = investment.total_amount;
        }
      }
      
      // If resetting to "offen", set paid_amount to 0
      if (paymentStatus === 'offen') {
        payload.paid_amount = 0;
      }
      
      await axios.put(`${API}/investments/${investmentId}/payment`, payload);
      toast.success('Zahlungsstatus aktualisiert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Zahlungsstatus');
    }
  };

  const handlePartialPaymentSubmit = async () => {
    try {
      const newPayment = parseFloat(partialPaymentData.newPaymentAmount);
      
      if (isNaN(newPayment) || newPayment <= 0) {
        toast.error('Bitte geben Sie einen gültigen Betrag ein');
        return;
      }
      
      // Add new payment to existing paid amount
      const totalPaidAmount = partialPaymentData.currentPaidAmount + newPayment;
      
      await axios.put(`${API}/investments/${partialPaymentData.investmentId}/payment`, {
        payment_status: 'teilweise_bezahlt',
        paid_amount: totalPaidAmount,
        payment_date: new Date().toISOString()
      });
      toast.success('Teilzahlung erfasst');
      setShowPartialPaymentDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Erfassen der Teilzahlung');
    }
  };

  const handleCancelInvestment = async (investmentId, projectTitle, shares) => {
    if (!window.confirm(`Möchten Sie diese Investition wirklich stornieren?\n\nProjekt: ${projectTitle}\nAnteile: ${shares}\n\nDie Anteile werden dem Projekt wieder zur Verfügung gestellt und der Benutzer wird per E-Mail benachrichtigt.`)) {
      return;
    }

    try {
      await axios.put(`${API}/investments/${investmentId}/admin-cancel`);
      toast.success('Investition erfolgreich storniert. Benutzer wurde benachrichtigt.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Stornieren der Investition');
    }
  };

  const handleDeleteInvestment = async (investmentId, investmentNumber, shares) => {
    if (!window.confirm(`⚠️ WARNUNG: Investition permanent löschen?\n\nInvestment-Nr: ${investmentNumber}\nAnteile: ${shares}\n\nDies kann NICHT rückgängig gemacht werden!\nDie Anteile werden dem Projekt zurückgegeben.\n\nWirklich löschen?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/investments/${investmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Investment ${investmentNumber} erfolgreich gelöscht. ${shares} Anteile wurden zurückgegeben.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen der Investition');
    }
  };

  const handleUpdateProfitEligible = async (investmentId, profitEligible) => {
    try {
      await axios.put(`${API}/investments/${investmentId}/profit-eligible`, {
        profit_eligible: profitEligible
      });
      toast.success(profitEligible ? 'Als gewinnberechtigt markiert' : 'Ertragberechtigung entfernt');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren der Ertragberechtigung');
    }
  };

  const handleCreateProfit = async (e) => {
    e.preventDefault();
    if (!profitForm.project_id || !profitForm.month || !profitForm.amount) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    try {
      await axios.post(`${API}/profits`, {
        project_id: profitForm.project_id,
        month: profitForm.month,
        amount: parseFloat(profitForm.amount)
      });
      toast.success('Ertrag erfolgreich eingetragen');
      setProfitForm({ project_id: '', month: '', amount: '' });
      fetchAllProfits();
    } catch (error) {
      toast.error('Fehler beim Eintragen des Ertrags');
    }
  };

  const handleRecalculateProfits = async () => {
    if (!window.confirm('Ertragverteilung neu berechnen?\n\nErträge werden nur auf gewinnberechtigte Investitionen verteilt.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/profits/recalculate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.message}\n\n` +
        `📊 ${response.data.stats.projects_processed} Projekte verarbeitet\n` +
        `💰 ${response.data.stats.paid_investments_count} bezahlte Investments\n` +
        `✅ ${response.data.stats.profit_eligible_count} gewinnberechtigte Investments\n` +
        `❌ ${response.data.stats.unpaid_investments_count} unbezahlte Investments`
      );
      
      fetchAllProfits();
    } catch (error) {
      toast.error('Fehler beim Neuberechnen der Erträge');
    }
  };

  const handleUpdateProfit = async () => {
    if (!editingProfit) return;
    
    try {
      const response = await axios.put(
        `${API}/profits/${editingProfit.id}`,
        profitEditForm
      );
      
      toast.success(response.data.message + 
        (response.data.notifications_sent > 0 
          ? `\n📧 ${response.data.notifications_sent} neue Benachrichtigungen versendet` 
          : '\n✅ Keine neuen Benachrichtigungen erforderlich'));
      
      // If the distribution was expanded, reload it
      if (expandedProfits[editingProfit.id]) {
        try {
          const distResponse = await axios.get(`${API}/profits/${editingProfit.id}/distribution`);
          setProfitDistributions(prev => ({
            ...prev,
            [editingProfit.id]: distResponse.data
          }));
        } catch (error) {
          console.error('Failed to refresh profit distribution:', error);
        }
      }
      
      setShowProfitEditModal(false);
      setEditingProfit(null);
      setProfitEditForm({ amount: '', resend_notifications: true });
      fetchAllProfits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Ertrags');
    }
  };

  const handleMigrateProfitEligible = async () => {
    if (!window.confirm('Alle bezahlten Investitionen als gewinnberechtigt markieren?\n\nDies setzt profit_eligible=true für alle bezahlten und nicht stornierten Investitionen.')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/investments/migrate-profit-eligible`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Fehler bei der Migration');
    }
  };

  const fetchAllProfits = async () => {
    try {
      const response = await axios.get(`${API}/profits`);
      setAllProfits(response.data);
    } catch (error) {
      console.error('Failed to fetch profits:', error);
    }
  };

  const handleConfirmPayment = async (investmentId) => {
    if (!window.confirm('Haben Sie die Zahlung wirklich durchgeführt?\n\nDer Administrator wird benachrichtigt und überprüft den Zahlungseingang.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/investments/${investmentId}/confirm-payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zahlungsbestätigung erfolgreich! Administrator wurde benachrichtigt.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Zahlungsbestätigung');
    }
  };

  const handleConfirmPaymentStage = async (investmentId, stage) => {
    if (!window.confirm(`Haben Sie Zahlungsstufe ${stage} wirklich bezahlt?\n\nDer Administrator wird benachrichtigt und überprüft den Zahlungseingang.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/investments/${investmentId}/confirm-payment-stage/${stage}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Zahlungsstufe ${stage} bestätigt! Administrator wurde benachrichtigt.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Zahlungsbestätigung');
    }
  };

  const handleAdminConfirmStage = async (investmentId, stage) => {
    if (!window.confirm(`Zahlungsstufe ${stage} als vollständig bezahlt markieren?\n\nDer User wird per E-Mail benachrichtigt und die nächste Stufe wird freigeschaltet.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/investments/${investmentId}/admin-confirm-stage/${stage}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Zahlungsstufe ${stage} erfolgreich bestätigt!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Bestätigung');
    }
  };

  const handleAdminPartialConfirmStage = async (investmentId, stage) => {
    const amount = window.prompt(`Welcher Betrag wurde für Stufe ${stage} bezahlt?\n\nGeben Sie den Betrag in Euro ein (z.B. 10000):`);
    
    if (!amount || isNaN(amount)) {
      if (amount !== null) {
        toast.error('Bitte geben Sie einen gültigen Betrag ein');
      }
      return;
    }

    const numAmount = parseFloat(amount);
    
    if (!window.confirm(`Teilzahlung von ${numAmount.toLocaleString('de-DE')}€ für Stufe ${stage} bestätigen?\n\nDer User wird benachrichtigt, dass eine Teilzahlung eingegangen ist.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Find investment in current investments array
      const investment = investments.find(inv => inv.id === investmentId);
      if (!investment) {
        toast.error('Investment nicht gefunden');
        return;
      }
      
      // Calculate new paid_amount based on partial payment
      const currentPaid = investment.paid_amount || 0;
      const newPaidAmount = currentPaid + numAmount;
      
      // Update paid_amount (which will trigger auto-sync)
      await axios.put(`${API}/investments/${investmentId}/payment`, {
        payment_status: 'teilweise_bezahlt',
        paid_amount: newPaidAmount,
        payment_notes: `Teilzahlung Stufe ${stage}: ${numAmount.toLocaleString('de-DE')}€`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Teilzahlung von ${numAmount.toLocaleString('de-DE')}€ erfolgreich verbucht!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Teilzahlung');
    }
  };

  const handleRequestCancellation = async (investmentId) => {
    const reason = prompt('Bitte geben Sie einen Grund für die Stornierung an:');
    if (!reason || reason.trim() === '') {
      toast.error('Grund für Stornierung ist erforderlich');
      return;
    }

    if (!window.confirm('Möchten Sie wirklich eine Stornierung für diese Investition beantragen?\n\nDie Entscheidung liegt beim Administrator.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/investments/${investmentId}/cancel-request`, {
        reason: reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stornierungsanfrage erfolgreich eingereicht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen der Stornierungsanfrage');
    }
  };

  const handleCancellationDecision = async (investmentId, decision) => {
    const confirmMessage = decision === 'approved' 
      ? 'Möchten Sie die Stornierung wirklich genehmigen?\n\nDie Anteile werden dem Projekt-Pool zurückgegeben.'
      : 'Möchten Sie die Stornierung wirklich ablehnen?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/investments/${investmentId}/cancel-decision`, {
        decision: decision
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(decision === 'approved' ? 'Stornierung genehmigt' : 'Stornierung abgelehnt');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Entscheidung');
    }
  };

  const handleSaveInvestmentBankDetails = async () => {
    if (!editingInvestmentBank) return;
    
    // Validation
    if (!investmentBankForm.iban || !investmentBankForm.account_holder) {
      toast.error('Bitte füllen Sie mindestens IBAN und Kontoinhaber aus');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/investments/${editingInvestmentBank}/bank-details`, 
        investmentBankForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Bankverbindung erfolgreich gespeichert');
      setEditingInvestmentBank(null);
      setInvestmentBankForm({ iban: '', bic: '', account_holder: '', bank_name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Bankverbindung');
    }
  };

  const handleEditInvestmentBank = (investment) => {
    setEditingInvestmentBank(investment.id);
    setInvestmentBankForm({
      iban: investment.bank_details?.iban || '',
      bic: investment.bank_details?.bic || '',
      account_holder: investment.bank_details?.account_holder || '',
      bank_name: investment.bank_details?.bank_name || ''
    });
  };

  const handlePrintProfits = async () => {
    // Load profit data for all users (Admin view)
    let allDistributions = [];
    
    toast.info('Lade Ertragaussch├╝ttungsdaten...');
    
    try {
      // Get all users' profit data from new admin endpoint
      const response = await axios.get(`${API}/profits/all-users-by-project`);
      const allUsersProfits = response.data;
      
      // Transform the data into allDistributions format
      allUsersProfits.forEach(projectProfit => {
        Object.entries(projectProfit.profits_by_year || {}).forEach(([year, yearData]) => {
          // yearData.months is an ARRAY
          (yearData.months || []).forEach(monthData => {
            allDistributions.push({
              month: monthData.month,
              amount: monthData.amount,
              project_id: projectProfit.project_id,
              project_title: projectProfit.project_title,
              project_image: projectProfit.project_image,
              user_id: projectProfit.user_id,  // User ID from the data
              shares: projectProfit.total_shares,
              total_invested: projectProfit.total_invested
            });
          });
        });
      });
      
      // Filter profits based on date range and user/project
      let filteredProfits = allDistributions.filter(profit => {
        // profit.month is in format "2025-11", printFilters dates are in format "2025-11"
        // Direct string comparison works for yyyy-mm format
        if (printFilters.startDate && profit.month < printFilters.startDate) return false;
        if (printFilters.endDate && profit.month > printFilters.endDate) return false;
        
        if (printFilters.filterBy === 'user' && printFilters.selectedId) {
          return profit.user_id === printFilters.selectedId;
        } else if (printFilters.filterBy === 'project' && printFilters.selectedId) {
          return profit.project_id === printFilters.selectedId;
        }
        
        return true;
      });

    // Generate print content
    if (filteredProfits.length === 0) {
      toast.error('Keine Erträge im ausgewählten Zeitraum');
      return;
    }

    // Group by user and then by project
    const groupedByUser = {};
    filteredProfits.forEach(profit => {
      if (!groupedByUser[profit.user_id]) {
        groupedByUser[profit.user_id] = {
          user: users.find(u => u.id === profit.user_id),
          projects: {},
          totalAmount: 0
        };
      }
      if (!groupedByUser[profit.user_id].projects[profit.project_id]) {
        groupedByUser[profit.user_id].projects[profit.project_id] = {
          project: projects.find(p => p.id === profit.project_id) || { title: profit.project_title },
          profits: [],
          totalAmount: 0
        };
      }
      groupedByUser[profit.user_id].projects[profit.project_id].profits.push(profit);
      groupedByUser[profit.user_id].projects[profit.project_id].totalAmount += profit.amount;
      groupedByUser[profit.user_id].totalAmount += profit.amount;
    });

    // Create print window
    const printWindow = window.open('', '_blank');
    
    // Format month strings for display (convert "2025-11" to "November 2025")
    const formatMonth = (monthStr) => {
      if (!monthStr) return null;
      const [year, month] = monthStr.split('-');
      const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    };
    
    const startDateStr = printFilters.startDate ? formatMonth(printFilters.startDate) : 'Anfang';
    const endDateStr = printFilters.endDate ? formatMonth(printFilters.endDate) : 'Heute';

    let printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ertrags-Auszahlungen ${startDateStr} - ${endDateStr}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
            font-size: 11px;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 3px solid #0066cc;
            padding-bottom: 15px;
          }
          .header h1 {
            color: #0066cc;
            margin: 0;
            font-size: 20px;
          }
          .header p {
            margin: 3px 0;
            color: #666;
            font-size: 10px;
          }
          .user-section {
            margin-bottom: 30px;
            page-break-inside: avoid;
            border: 2px solid #0066cc;
            border-radius: 8px;
            overflow: hidden;
          }
          .user-header {
            background: linear-gradient(135deg, #0066cc 0%, #004c99 100%);
            color: white;
            padding: 12px 15px;
          }
          .user-header h2 {
            margin: 0;
            font-size: 16px;
            font-weight: bold;
          }
          .user-header p {
            margin: 3px 0 0 0;
            opacity: 0.9;
            font-size: 11px;
          }
          .user-total {
            background: #e7f3ff;
            padding: 10px 15px;
            border-bottom: 2px solid #0066cc;
            font-weight: bold;
          }
          .user-total-amount {
            font-size: 18px;
            color: #28a745;
          }
          .project-section {
            margin: 15px;
            border: 1px solid #ddd;
            border-radius: 6px;
            overflow: hidden;
          }
          .project-header {
            background: #f8f9fa;
            padding: 10px 12px;
            border-bottom: 2px solid #0066cc;
          }
          .project-header h3 {
            margin: 0;
            color: #0066cc;
            font-size: 14px;
            font-weight: bold;
          }
          .project-total {
            font-size: 16px;
            font-weight: bold;
            color: #28a745;
            margin-top: 5px;
          }
          .profit-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }
          .profit-table th {
            background: #f8f9fa;
            padding: 8px 6px;
            text-align: left;
            font-weight: 600;
            border-bottom: 2px solid #dee2e6;
            font-size: 10px;
          }
          .profit-table td {
            padding: 6px;
            border-bottom: 1px solid #dee2e6;
          }
          .profit-table tr:hover {
            background: #f8f9fa;
          }
          .amount {
            font-weight: 600;
            color: #28a745;
            text-align: right;
          }
          .shares {
            text-align: center;
          }
          .percentage {
            text-align: center;
          }
          @media print {
            body {
              margin: 10px;
              font-size: 9px;
            }
            .user-section {
              page-break-inside: avoid;
            }
            .profit-table {
              font-size: 8px;
            }
            .profit-table th, .profit-table td {
              padding: 4px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Ertrags-Auszahlungen Übersicht</h1>
          <p>Zeitraum: ${startDateStr} - ${endDateStr}</p>
          <p>Erstellt am: ${new Date().toLocaleDateString('de-DE')} um ${new Date().toLocaleTimeString('de-DE')}</p>
          <p>Anzahl Investoren: ${Object.keys(groupedByUser).length} | Gesamtauszahlungen: ${filteredProfits.length}</p>
        </div>
    `;

    Object.values(groupedByUser).forEach(userData => {
      const user = userData.user;
      
      printContent += `
        <div class="user-section">
          <div class="user-header">
            <h2>👤 ${user?.username || 'Unbekannter Investor'}</h2>
            <p>📧 ${user?.email || 'Keine E-Mail'}</p>
          </div>
          <div class="user-total">
            Gesamtauszahlung an ${user?.username || 'Investor'}: 
            <span class="user-total-amount">${userData.totalAmount.toFixed(2)} €</span>
          </div>
      `;

      Object.values(userData.projects).forEach(projectData => {
        const project = projectData.project;

        printContent += `
          <div class="project-section">
            <div class="project-header">
              <h3>📊 ${project?.title || 'Unbekanntes Projekt'}</h3>
              <div class="project-total">Projektbetrag: ${projectData.totalAmount.toFixed(2)} €</div>
            </div>
            <table class="profit-table">
              <thead>
                <tr>
                  <th style="width: 15%;">Auszahlungsdatum</th>
                  <th style="width: 30%;">Beschreibung</th>
                  <th style="width: 10%;" class="shares">Anteile</th>
                  <th style="width: 10%;" class="percentage">Prozent</th>
                  <th style="width: 10%;" class="percentage">Ertrag/Anteil</th>
                  <th style="width: 15%;" class="amount">Betrag</th>
                  <th style="width: 10%;">Status</th>
                </tr>
              </thead>
              <tbody>
        `;

        projectData.profits.forEach(profit => {
          // Parse month (format: "2025-11") to display date
          const [year, month] = profit.month.split('-');
          const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
          const monthName = monthNames[parseInt(month) - 1] || month;
          const displayDate = `${monthName} ${year}`;
          
          const profitPerShare = profit.shares > 0 ? (profit.amount / profit.shares).toFixed(2) : '0.00';
          const percentage = profit.total_invested > 0 ? ((profit.amount / profit.total_invested) * 100).toFixed(2) : '0.00';
          
          printContent += `
            <tr>
              <td>${displayDate}</td>
              <td><strong>Ertragausschüttung</strong><br/>
                  <small style="color: #666;">${profit.month}</small></td>
              <td class="shares"><strong>${profit.shares || '0'}</strong></td>
              <td class="percentage">${percentage}%</td>
              <td class="percentage"><strong>${profitPerShare} €</strong></td>
              <td class="amount"><strong>${profit.amount.toFixed(2)} €</strong></td>
              <td style="font-size: 9px;">✓ Ausgezahlt</td>
            </tr>
          `;
        });

        printContent += `
                <tr style="background: #e7f3ff; font-weight: bold;">
                  <td colspan="5" style="text-align: right; padding-right: 10px;">Projektsumme:</td>
                  <td class="amount" style="font-size: 12px;">${projectData.totalAmount.toFixed(2)} €</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      });

      printContent += `</div>`;
    });

    // Grand total
    const grandTotal = filteredProfits.reduce((sum, p) => sum + p.amount, 0);
    printContent += `
      <div style="margin-top: 20px; padding: 15px; background: #e7f3ff; border: 2px solid #0066cc; border-radius: 8px; text-align: center;">
        <h3 style="margin: 0; color: #0066cc;">Gesamtsumme aller Auszahlungen</h3>
        <div style="font-size: 24px; font-weight: bold; color: #28a745; margin-top: 10px;">${grandTotal.toFixed(2)} €</div>
      </div>
    `;

    printContent += `
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
    }, 250);
    
    setShowPrintModal(false);
    } catch (error) {
      console.error('Error loading profit data:', error);
      toast.error('Fehler beim Laden der Ertragausschüttungsdaten');
    }
  };

  const handleDeleteProfit = async (profitId) => {
    if (!window.confirm('Möchten Sie diesen Ertrag-Eintrag wirklich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      await axios.delete(`${API}/profits/${profitId}`);
      toast.success('Ertrag erfolgreich gelöscht');
      fetchAllProfits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleSaveBankAccount = async (e) => {
    e.preventDefault();
    
    if (!bankAccountForm.account_holder || !bankAccountForm.iban) {
      toast.error('Kontoinhaber und IBAN sind erforderlich');
      return;
    }

    try {
      await axios.post(`${API}/user-bank-accounts`, bankAccountForm);
      toast.success('Auszahlungskonto gespeichert');
      setEditingBankAccount(null);
      setBankAccountForm({
        project_id: '',
        account_holder: '',
        iban: '',
        bic: '',
        bank_name: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const startEditingBankAccount = (projectId, projectTitle) => {
    const existing = userBankAccounts.find(acc => acc.project_id === projectId);
    if (existing) {
      setBankAccountForm({
        project_id: projectId,
        account_holder: existing.account_holder || '',
        iban: existing.iban || '',
        bic: existing.bic || '',
        bank_name: existing.bank_name || ''
      });
    } else {
      setBankAccountForm({
        project_id: projectId,
        account_holder: user?.username || '',
        iban: '',
        bic: '',
        bank_name: ''
      });
    }
    setEditingBankAccount({ project_id: projectId, project_title: projectTitle });
  };

  const cancelEditingBankAccount = () => {
    setEditingBankAccount(null);
    setBankAccountForm({
      project_id: '',
      account_holder: '',
      iban: '',
      bic: '',
      bank_name: ''
    });
  };

  const fetchPendingBankAccounts = async () => {
    try {
      const response = await axios.get(`${API}/user-bank-accounts/all/pending`);
      setPendingBankAccounts(response.data);
    } catch (error) {
      console.error('Error fetching pending bank accounts:', error);
    }
  };

  const handleApproveBankAccount = async (userId, projectId) => {
    if (!window.confirm('Möchten Sie dieses Auszahlungskonto genehmigen?')) {
      return;
    }

    try {
      await axios.put(`${API}/user-bank-accounts/${userId}/${projectId}/approve`);
      toast.success('Auszahlungskonto genehmigt');
      fetchPendingBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Genehmigen');
    }
  };

  const handleRejectBankAccount = async (userId, projectId) => {
    if (!window.confirm('Möchten Sie dieses Auszahlungskonto ablehnen?')) {
      return;
    }

    try {
      await axios.put(`${API}/user-bank-accounts/${userId}/${projectId}/reject`);
      toast.success('Auszahlungskonto abgelehnt');
      fetchPendingBankAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  };

  const handleExportReport = async () => {
    if (!exportConfig.entity_id) {
      toast.error('Bitte wählen Sie einen User oder ein Projekt aus');
      return;
    }

    try {
      const params = new URLSearchParams({
        export_type: exportConfig.type,
        entity_id: exportConfig.entity_id
      });

      if (exportConfig.start_date) {
        params.append('start_date', new Date(exportConfig.start_date).toISOString());
      }
      if (exportConfig.end_date) {
        params.append('end_date', new Date(exportConfig.end_date).toISOString());
      }

      const response = await axios.get(`${API}/reports/export?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const filename = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 
                      `bericht_${Date.now()}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Bericht erfolgreich exportiert');
      setShowExportModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Exportieren');
    }
  };

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin')) {
      fetchAllProfits();
      fetchPendingBankAccounts();
    }
  }, [user]);

  const toggleProjectInvestors = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleProfitDistribution = async (profitId) => {
    // Toggle expansion
    setExpandedProfits(prev => ({
      ...prev,
      [profitId]: !prev[profitId]
    }));

    // If expanding and we don't have the distribution data yet, fetch it
    if (!expandedProfits[profitId] && !profitDistributions[profitId]) {
      setLoadingDistribution(prev => ({ ...prev, [profitId]: true }));
      try {
        const response = await axios.get(`${API}/profits/${profitId}/distribution`);
        setProfitDistributions(prev => ({
          ...prev,
          [profitId]: response.data
        }));
      } catch (error) {
        console.error('Failed to fetch profit distribution:', error);
        toast.error('Fehler beim Laden der Ertragverteilung');
      } finally {
        setLoadingDistribution(prev => ({ ...prev, [profitId]: false }));
      }
    }
  };

  const toggleInvestmentDetails = (projectId) => {
    setExpandedInvestments(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const toggleYearDetails = (key) => {
    setExpandedYears(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'offen': { color: 'bg-red-100 text-red-800 border-red-200', label: 'Offen' },
      'teilweise_bezahlt': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Teilweise bezahlt' },
      'bezahlt': { color: 'bg-green-100 text-green-800 border-green-200', label: 'Bezahlt' }
    };
    const config = statusConfig[status] || statusConfig['offen'];
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${config.color}`}>{config.label}</span>;
  };

  const getFullInvestmentStatus = (investment) => {
    const badges = [];
    
    // Payment Status Badge
    const paymentStatus = investment.payment_status || 'offen';
    const paymentConfig = {
      'offen': { color: 'bg-red-100 text-red-800 border-red-200', label: 'Offen' },
      'teilweise_bezahlt': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Teilweise bezahlt' },
      'bezahlt': { color: 'bg-green-100 text-green-800 border-green-200', label: 'Bezahlt' }
    };
    const paymentBadgeConfig = paymentConfig[paymentStatus] || paymentConfig['offen'];
    badges.push(
      <span key="payment" className={`px-2 py-1 rounded-full text-xs font-semibold border ${paymentBadgeConfig.color}`}>
        {paymentBadgeConfig.label}
      </span>
    );

    // Cancellation Status Badge
    if (investment.cancellation_status === 'requested') {
      badges.push(
        <span key="cancellation" className="px-2 py-1 rounded-full text-xs font-semibold border bg-orange-100 text-orange-800 border-orange-200">
          Stornierung beantragt
        </span>
      );
    } else if (investment.cancellation_status === 'approved') {
      badges.push(
        <span key="cancellation" className="px-2 py-1 rounded-full text-xs font-semibold border bg-red-600 text-white border-red-700">
          Storniert
        </span>
      );
    } else if (investment.cancellation_status === 'rejected') {
      badges.push(
        <span key="cancellation" className="px-2 py-1 rounded-full text-xs font-semibold border bg-gray-100 text-gray-800 border-gray-200">
          Stornierung abgelehnt
        </span>
      );
    }

    return <div className="flex flex-wrap gap-1">{badges}</div>;
  };

  const formatMonthYear = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const groupInvestmentsByUser = () => {
    const grouped = {};
    investments.forEach(inv => {
      if (!grouped[inv.user_id]) {
        const investmentUser = users.find(u => u.id === inv.user_id);
        grouped[inv.user_id] = {
          user_id: inv.user_id,
          user_name: investmentUser?.username || 'Unbekannt',
          user_email: investmentUser?.email || '',
          investments: [],
          total_amount: 0,
          total_shares: 0,
          profit_eligible_shares: 0
        };
      }
      grouped[inv.user_id].investments.push(inv);
      grouped[inv.user_id].total_amount += inv.total_amount || 0;
      grouped[inv.user_id].total_shares += inv.shares || 0;
      
      // Count profit eligible shares (paid, not cancelled, profit_eligible)
      if (inv.payment_status === 'bezahlt' && 
          inv.cancellation_status !== 'approved' && 
          inv.status !== 'cancelled' &&
          inv.payment_status !== 'storniert' &&
          inv.profit_eligible === true) {
        grouped[inv.user_id].profit_eligible_shares += inv.shares || 0;
      }
    });
    return Object.values(grouped);
  };

  const groupInvestmentsByProject = () => {
    const grouped = {};
    investments.forEach(inv => {
      if (!grouped[inv.project_id]) {
        const project = projects.find(p => p.id === inv.project_id);
        grouped[inv.project_id] = {
          project_id: inv.project_id,
          project_title: project?.title || 'Unbekannt',
          investments: [],
          total_amount: 0,
          total_shares: 0
        };
      }
      grouped[inv.project_id].investments.push(inv);
      grouped[inv.project_id].total_amount += inv.total_amount || 0;
      grouped[inv.project_id].total_shares += inv.shares || 0;
    });
    return Object.values(grouped);
  };

  const getProjectInvestors = (projectId) => {
    // Filter by project_id and valid payment status (not cancelled)
    const projectInvestments = investments.filter(inv => 
      inv.project_id === projectId && 
      inv.cancellation_status !== 'approved'  // Exclude cancelled investments
    );
    
    // Debug logging
    console.log('Project ID:', projectId);
    console.log('Total investments:', investments.length);
    console.log('Project investments:', projectInvestments.length);
    console.log('Total users:', users.length);
    
    return projectInvestments.map(inv => {
      const investor = users.find(u => u.id === inv.user_id);
      return {
        ...inv,
        username: investor?.username || 'Unbekannt',
        email: investor?.email || 'N/A'
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg text-slate-600">Dashboard wird geladen...</div>
      </div>
    );
  }

  // Calculate total investment and profits
  const totalInvested = investments.reduce((sum, inv) => sum + inv.total_amount, 0);
  const totalProfit = profits.reduce((sum, p) => sum + p.profit, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-fadeIn">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Dashboard</h1>
            <p className="text-lg text-slate-600">Willkommen zurück, {user.username}!</p>
            <span className="inline-block mt-2 px-3 py-1 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-full text-sm font-semibold">
              {user.role.toUpperCase()}
            </span>
          </div>

          {user.role === 'user' && (
            <div>
              {/* User Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Gesamt Investiert</p>
                      <p className="text-3xl font-bold text-slate-900">€{totalInvested.toLocaleString()}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                      <Package className="text-blue-600" size={24} />
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Gesamt Erträge</p>
                      <p className="text-3xl font-bold text-green-600">€{totalProfit.toLocaleString()}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center">
                      <TrendingUp className="text-green-600" size={24} />
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Aktive Investitionen</p>
                      <p className="text-3xl font-bold text-slate-900">{investments.length}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
                      <Check className="text-slate-600" size={24} />
                    </div>
                  </div>
                </div>
              </div>

              <Tabs defaultValue="investments" className="w-full">
                <TabsList className="mb-6">
                  <TabsTrigger value="investments" data-testid="tab-investments">Meine Investitionen</TabsTrigger>
                  <TabsTrigger value="cancelled" data-testid="tab-cancelled">Stornierte Investitionen</TabsTrigger>
                  <TabsTrigger value="profits" data-testid="tab-profits">Erträge</TabsTrigger>
                  <TabsTrigger value="interests" data-testid="tab-interests">Interessen</TabsTrigger>
                </TabsList>

                <TabsContent value="investments">
                  <div className="card">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Meine Investitionen</h2>
                    {groupedInvestments.length === 0 ? (
                      <p className="text-slate-600">Sie haben noch keine Investitionen getätigt.</p>
                    ) : (
                      <div className="space-y-4">
                        {groupedInvestments.map((group) => (
                          <div key={group.project_id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`grouped-investment-${group.project_id}`}>
                            {/* Grouped Summary */}
                            <div 
                              className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 cursor-pointer hover:from-slate-100 hover:to-slate-200 transition-all"
                              onClick={() => toggleInvestmentDetails(group.project_id)}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                  {group.project_image && (
                                    <img src={group.project_image} alt={group.project_title} className="w-16 h-16 rounded-lg object-cover" />
                                  )}
                                  <div>
                                    <p className="font-bold text-slate-900 text-lg">{group.project_title}</p>
                                    <p className="text-sm text-slate-600">
                                      {group.investment_count} {group.investment_count === 1 ? 'Investition' : 'Investitionen'} • {group.total_shares} Anteile
                                    </p>
                                    {(() => {
                                      const paidInvestments = group.investments.filter(inv => 
                                        inv.payment_status === 'bezahlt' && inv.cancellation_status !== 'approved'
                                      );
                                      const paidCount = paidInvestments.length;
                                      const paidSum = paidInvestments.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
                                      
                                      if (paidCount > 0) {
                                        return (
                                          <p className="text-sm text-green-600 font-medium mt-1">
                                            {paidCount} aktive {paidCount === 1 ? 'Investition' : 'Investitionen'} • €{paidSum.toLocaleString()}
                                          </p>
                                        );
                                      }
                                      return null;
                                    })()}
                                    
                                    {/* Project Progress Bar */}
                                    {(() => {
                                      const project = projects.find(p => p.id === group.project_id);
                                      if (project && project.project_progress > 0) {
                                        // Determine color based on progress
                                        let barColor = 'bg-purple-500'; // Default: lila for low progress
                                        let textColor = 'text-purple-600';
                                        
                                        if (project.project_progress >= 75) {
                                          barColor = 'bg-green-500'; // Grün für hohen Fortschritt
                                          textColor = 'text-green-600';
                                        } else if (project.project_progress >= 50) {
                                          barColor = 'bg-lime-500'; // Hellgrün für mittleren Fortschritt
                                          textColor = 'text-lime-600';
                                        } else if (project.project_progress >= 25) {
                                          barColor = 'bg-yellow-500'; // Gelb für niedrig-mittleren Fortschritt
                                          textColor = 'text-yellow-600';
                                        }
                                        
                                        return (
                                          <div className="mt-2">
                                            <div className="flex items-center gap-2 text-xs text-slate-600 mb-1">
                                              <span>Projektfortschritt:</span>
                                              <span className={`font-semibold ${textColor}`}>{project.project_progress.toFixed(1)}%</span>
                                            </div>
                                            <div className="w-48 bg-slate-200 rounded-full h-1.5">
                                              <div
                                                className={`${barColor} h-1.5 rounded-full transition-all duration-500`}
                                                style={{ width: `${Math.min(project.project_progress, 100)}%` }}
                                              />
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                                <div className="text-right flex items-center gap-3">
                                  <div>
                                    <p className="text-xs text-slate-500 mb-1">Gesamtsumme</p>
                                    <p className="text-2xl font-bold text-green-600">€{group.total_amount.toLocaleString()}</p>
                                  </div>
                                  <span className="text-slate-400 text-2xl">{expandedInvestments[group.project_id] ? '▼' : '▶'}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Individual Investments */}
                            {expandedInvestments[group.project_id] && (
                              <div className="bg-white p-4 space-y-3 border-t border-slate-200">
                                <h3 className="font-semibold text-slate-700 mb-3">Einzelinvestitionen:</h3>
                                {group.investments.map((inv, idx) => (
                                  <div key={inv.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-all">
                                    <div className="flex justify-between items-start mb-3">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <p className="text-sm font-mono text-slate-700 bg-white px-2 py-1 rounded border border-slate-300">
                                            {inv.investment_number || `INV-${idx + 1}`}
                                          </p>
                                          {getPaymentStatusBadge(inv.payment_status)}
                                          {inv.cancellation_status === 'requested' && (
                                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                                              Stornierung beantragt
                                            </span>
                                          )}
                                          {inv.cancellation_status === 'approved' && (
                                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                                              Storniert
                                            </span>
                                          )}
                                          {inv.cancellation_status === 'rejected' && (
                                            <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                              Stornierung abgelehnt
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-sm text-slate-600">Anteile: {inv.shares}</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                          Datum: {new Date(inv.created_at).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                        {inv.cancellation_reason && (
                                          <p className="text-xs text-slate-600 mt-2 italic">
                                            Grund: {inv.cancellation_reason}
                                          </p>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <p className="text-lg font-bold text-slate-900">€{inv.total_amount.toLocaleString()}</p>
                                        <p className="text-xs text-slate-500">
                                          Preis/Anteil: €{(inv.price_per_share || (inv.total_amount / inv.shares)).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          {inv.price_per_share && group.project?.price_per_share && inv.price_per_share !== group.project.price_per_share && (
                                            <span className="text-blue-600 font-medium"> (historisch)</span>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Payment Progress Bar - Full Width */}
                                    {inv.paid_amount !== undefined && inv.paid_amount >= 0 && (
                                      <div className="w-full mt-4 pt-4 border-t border-slate-200">
                                        <div className="flex justify-between items-center mb-2">
                                          <span className="text-sm text-slate-600 font-medium">
                                            €{(inv.paid_amount || 0).toLocaleString()} bezahlt
                                          </span>
                                          <span className="text-sm font-bold text-blue-600">
                                            {((inv.paid_amount || 0) / inv.total_amount * 100).toFixed(1)}%
                                          </span>
                                        </div>
                                        <div className="relative h-10 bg-slate-200 rounded-full overflow-hidden">
                                          {/* Multi-colored Progress Bar based on stages */}
                                          {(() => {
                                            const project = projects.find(p => p.id === inv.project_id);
                                            const payment_stages = project?.payment_stages || [50, 30, 15, 5];
                                            const paidPercentage = (inv.paid_amount || 0) / inv.total_amount * 100;
                                            
                                            // If nothing paid, show red bar up to first stage
                                            if (paidPercentage === 0) {
                                              return (
                                                <div className="absolute top-0 left-0 h-full w-full bg-red-200 rounded-full" />
                                              );
                                            }
                                            
                                            // Calculate cumulative stages
                                            let cumulative = 0;
                                            const cumulativeStages = payment_stages.map(stage => {
                                              cumulative += stage;
                                              return cumulative;
                                            });
                                            
                                            // Determine which stage user is in
                                            let currentStage = 0;
                                            for (let i = 0; i < cumulativeStages.length; i++) {
                                              if (paidPercentage <= cumulativeStages[i]) {
                                                currentStage = i;
                                                break;
                                              }
                                            }
                                            
                                            // Define colors for each stage
                                            const stageColors = [
                                              'bg-yellow-200',  // Stufe 1: Hellgelb
                                              'bg-yellow-400',  // Stufe 2: Gelb
                                              'bg-yellow-400',  // Stufe 3: Gelb
                                              'bg-green-500'    // Stufe 4: Grün
                                            ];
                                            
                                            // Render colored segments
                                            return (
                                              <>
                                                {payment_stages.map((stage, idx) => {
                                                  const segmentStart = idx === 0 ? 0 : cumulativeStages[idx - 1];
                                                  const segmentEnd = cumulativeStages[idx];
                                                  const segmentWidth = segmentEnd - segmentStart;
                                                  
                                                  // Calculate how much of this segment is filled
                                                  let fillWidth = 0;
                                                  if (paidPercentage > segmentEnd) {
                                                    fillWidth = 100; // Fully filled
                                                  } else if (paidPercentage > segmentStart) {
                                                    fillWidth = ((paidPercentage - segmentStart) / segmentWidth) * 100;
                                                  } else {
                                                    fillWidth = 0; // Not filled
                                                  }
                                                  
                                                  return (
                                                    <div
                                                      key={idx}
                                                      className="absolute top-0 h-full"
                                                      style={{ 
                                                        left: `${segmentStart}%`, 
                                                        width: `${segmentWidth}%` 
                                                      }}
                                                    >
                                                      <div
                                                        className={`h-full transition-all duration-500 ${stageColors[idx]}`}
                                                        style={{ width: `${fillWidth}%` }}
                                                      />
                                                    </div>
                                                  );
                                                })}
                                              </>
                                            );
                                          })()}
                                          
                                          {/* Payment Stage Markers */}
                                          {(() => {
                                            const project = projects.find(p => p.id === inv.project_id);
                                            const payment_stages = project?.payment_stages || [50, 30, 15, 5];
                                            let cumulative = 0;
                                            return payment_stages.map((stage, idx) => {
                                              cumulative += stage;
                                              if (idx === payment_stages.length - 1) return null;
                                              return (
                                                <div 
                                                  key={idx}
                                                  className="absolute top-0 bottom-0 flex items-center justify-center z-10"
                                                  style={{ left: `${cumulative}%`, transform: 'translateX(-50%)' }}
                                                  title={`Stufe ${idx + 1}: ${cumulative}%`}
                                                >
                                                  <div className="w-1 h-full bg-slate-700 opacity-80" />
                                                  <div className="absolute -top-4 text-base font-bold text-slate-700">
                                                    ▼
                                                  </div>
                                                </div>
                                              );
                                            });
                                          })()}
                                        </div>
                                        <div className="relative text-xs text-slate-500 mt-2 h-4">
                                          {/* 0% Label */}
                                          <span className="absolute left-0 font-medium">0%</span>
                                          
                                          {/* Dynamic Stage Labels */}
                                          {(() => {
                                            const project = projects.find(p => p.id === inv.project_id);
                                            const payment_stages = project?.payment_stages || [50, 30, 15, 5];
                                            let cumulative = 0;
                                            return payment_stages.map((stage, idx) => {
                                              cumulative += stage;
                                              return (
                                                <span 
                                                  key={idx}
                                                  className="absolute font-medium"
                                                  style={{ left: `${cumulative}%`, transform: 'translateX(-50%)' }}
                                                >
                                                  {cumulative}%
                                                </span>
                                              );
                                            });
                                          })()}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Payment Stage Descriptions */}
                                    {(() => {
                                      const project = projects.find(p => p.id === inv.project_id);
                                      const payment_stage_descriptions = project?.payment_stage_descriptions || ['', '', '', ''];
                                      const hasDescriptions = payment_stage_descriptions.some(desc => desc && desc.trim() !== '');
                                      
                                      if (!hasDescriptions) return null;
                                      
                                      return (
                                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                          <h5 className="text-sm font-semibold text-slate-900 mb-2">📋 Zahlungsstufen:</h5>
                                          <div className="space-y-1">
                                            {payment_stage_descriptions.map((desc, idx) => {
                                              if (!desc || desc.trim() === '') return null;
                                              const payment_stages = project?.payment_stages || [50, 30, 15, 5];
                                              // Use the investment's total_amount instead of project price
                                              const totalAmount = (inv.total_amount * payment_stages[idx] / 100).toFixed(2);
                                              return (
                                                <div key={idx} className="flex items-start gap-2 text-xs">
                                                  <span className="font-semibold text-slate-700 min-w-[100px]">
                                                    Stufe {idx + 1} ({payment_stages[idx]}% - {Number(totalAmount).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})}€):
                                                  </span>
                                                  <span className="text-slate-600">{desc}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    
                                    {/* Payment Stages - New System */}
                                    {(() => {
                                      const project = projects.find(p => p.id === inv.project_id);
                                      const payment_stages = project?.payment_stages || [50, 30, 15, 5];
                                      const payment_stages_status = inv.payment_stages_status || {};
                                      const current_stage = inv.payment_stage || 1;
                                      
                                      // Calculate completed stages
                                      const completed_stages = Object.keys(payment_stages_status).filter(
                                        stage => payment_stages_status[stage]?.admin_confirmed
                                      ).length;
                                      
                                      return (
                                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                          <div className="flex items-center justify-between mb-3">
                                            <h5 className="text-sm font-semibold text-slate-900">💳 Zahlungsstufen</h5>
                                            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                              {completed_stages} von 4 abgeschlossen
                                            </span>
                                          </div>
                                          
                                          {/* Visual Progress */}
                                          <div className="flex gap-2 mb-4">
                                            {[1, 2, 3, 4].map(stage => {
                                              const stage_status = payment_stages_status[stage.toString()] || {};
                                              const is_completed = stage_status.admin_confirmed;
                                              const is_waiting = stage_status.user_confirmed && !stage_status.admin_confirmed;
                                              const is_current = stage === current_stage && !is_completed;
                                              
                                              return (
                                                <div 
                                                  key={stage} 
                                                  className={`flex-1 h-3 rounded-full transition-all ${
                                                    is_completed ? 'bg-green-500' :
                                                    is_waiting ? 'bg-yellow-500 animate-pulse' :
                                                    is_current ? 'bg-blue-300' :
                                                    'bg-slate-300'
                                                  }`}
                                                  title={`Stufe ${stage}: ${payment_stages[stage-1]}%`}
                                                />
                                              );
                                            })}
                                          </div>
                                          
                                          {/* Stage Details */}
                                          <div className="space-y-2">
                                            {[1, 2, 3, 4].map(stage => {
                                              const stage_status = payment_stages_status[stage.toString()] || {};
                                              const is_completed = stage_status.admin_confirmed;
                                              const is_waiting = stage_status.user_confirmed && !stage_status.admin_confirmed;
                                              const is_current = stage === current_stage && !is_completed && !is_waiting;
                                              const stage_percentage = payment_stages[stage - 1];
                                              const stage_amount = (inv.total_amount * stage_percentage / 100).toFixed(2);
                                              
                                              // Calculate if this stage is covered by paid_amount (overpayment detection)
                                              const paid_amount = inv.paid_amount || 0;
                                              const cumulative_percentage = payment_stages.slice(0, stage).reduce((sum, p) => sum + p, 0);
                                              const cumulative_amount = (inv.total_amount * cumulative_percentage / 100).toFixed(2);
                                              const is_covered_by_payment = paid_amount >= parseFloat(cumulative_amount);
                                              
                                              // Calculate remaining amount for this SPECIFIC stage
                                              const prev_cumulative = payment_stages.slice(0, stage - 1).reduce((sum, p) => sum + p, 0);
                                              const prev_cumulative_amount = (inv.total_amount * prev_cumulative / 100);
                                              
                                              // If previous stage is fully paid, show only remaining for THIS stage
                                              let remaining_for_stage = 0;
                                              if (is_current && !is_covered_by_payment) {
                                                // Current stage, not yet fully covered
                                                const paid_beyond_prev = Math.max(0, paid_amount - prev_cumulative_amount);
                                                const this_stage_amount = parseFloat(cumulative_amount) - prev_cumulative_amount;
                                                remaining_for_stage = Math.max(0, this_stage_amount - paid_beyond_prev).toFixed(2);
                                              }
                                              
                                              // Show if completed, waiting, current, or covered by overpayment
                                              const show_stage = is_completed || is_waiting || is_current || (is_covered_by_payment && !is_completed);
                                              
                                              if (!show_stage) return null;
                                              
                                              return (
                                                <div 
                                                  key={stage}
                                                  className={`p-3 rounded-lg border ${
                                                    is_completed ? 'bg-green-50 border-green-300' :
                                                    is_waiting ? 'bg-yellow-50 border-yellow-300' :
                                                    is_covered_by_payment && !is_waiting ? 'bg-orange-50 border-orange-300' :
                                                    'bg-white border-blue-300'
                                                  }`}
                                                >
                                                  <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                      <p className="text-sm font-semibold text-slate-900">
                                                        Stufe {stage} ({stage_percentage}% - {stage_amount}€)
                                                      </p>
                                                      {is_completed && stage_status.admin_confirmed_at && (
                                                        <p className="text-xs text-green-600 mt-1">
                                                          ✅ Bestätigt am {new Date(stage_status.admin_confirmed_at).toLocaleDateString('de-DE')}
                                                        </p>
                                                      )}
                                                      {is_waiting && (
                                                        <p className="text-xs text-yellow-600 mt-1">
                                                          ⏱️ Warte auf Admin-Bestätigung
                                                        </p>
                                                      )}
                                                      {is_covered_by_payment && !is_completed && !is_waiting && (
                                                        <p className="text-xs text-orange-600 mt-1">
                                                          💰 Bereits bezahlt - Bitte bestätigen Sie die Zahlung
                                                        </p>
                                                      )}
                                                      {is_current && !is_covered_by_payment && (
                                                        <p className="text-xs text-blue-600 mt-1">
                                                          📋 Noch {remaining_for_stage}€ offen
                                                        </p>
                                                      )}
                                                    </div>
                                                    
                                                    {(is_current || (is_covered_by_payment && !is_completed && !is_waiting)) && (
                                                      <Button
                                                        size="sm"
                                                        className="bg-green-600 hover:bg-green-700 text-white ml-3"
                                                        onClick={() => handleConfirmPaymentStage(inv.id, stage)}
                                                      >
                                                        💳 Bezahlt
                                                      </Button>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          
                                          {/* Show cancellation button only if not all paid */}
                                          {completed_stages < 4 && (!inv.cancellation_status || inv.cancellation_status === 'none') && (
                                            <div className="mt-3 flex justify-end">
                                              <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="text-red-600 hover:bg-red-50"
                                                onClick={() => handleRequestCancellation(inv.id)}
                                              >
                                                Stornierung beantragen
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    
                                    {/* Bank Details Section - For payouts */}
                                    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-sm font-semibold text-slate-900">🏦 Bankverbindung für Auszahlungen</h5>
                                        {!editingInvestmentBank || editingInvestmentBank !== inv.id ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleEditInvestmentBank(inv)}
                                            className="h-7 px-2 text-xs"
                                          >
                                            <Edit className="w-3 h-3 mr-1" />
                                            {inv.bank_details?.iban ? 'Bearbeiten' : 'Hinzufügen'}
                                          </Button>
                                        ) : null}
                                      </div>
                                      
                                      {editingInvestmentBank === inv.id ? (
                                        <div className="space-y-2 mt-2">
                                          <div>
                                            <Label className="text-xs">Kontoinhaber*</Label>
                                            <Input
                                              value={investmentBankForm.account_holder}
                                              onChange={(e) => setInvestmentBankForm({...investmentBankForm, account_holder: e.target.value})}
                                              placeholder="Max Mustermann"
                                              className="h-8 text-xs"
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-xs">IBAN*</Label>
                                            <Input
                                              value={investmentBankForm.iban}
                                              onChange={(e) => setInvestmentBankForm({...investmentBankForm, iban: e.target.value})}
                                              placeholder="DE89 3704 0044 0532 0130 00"
                                              className="h-8 text-xs"
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-xs">BIC (optional)</Label>
                                            <Input
                                              value={investmentBankForm.bic}
                                              onChange={(e) => setInvestmentBankForm({...investmentBankForm, bic: e.target.value})}
                                              placeholder="COBADEFFXXX"
                                              className="h-8 text-xs"
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-xs">Bank (optional)</Label>
                                            <Input
                                              value={investmentBankForm.bank_name}
                                              onChange={(e) => setInvestmentBankForm({...investmentBankForm, bank_name: e.target.value})}
                                              placeholder="Commerzbank"
                                              className="h-8 text-xs"
                                            />
                                          </div>
                                          <div className="flex gap-2 mt-2">
                                            <Button
                                              size="sm"
                                              onClick={handleSaveInvestmentBankDetails}
                                              className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700"
                                            >
                                              Speichern
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => {
                                                setEditingInvestmentBank(null);
                                                setInvestmentBankForm({ iban: '', bic: '', account_holder: '', bank_name: '' });
                                              }}
                                              className="h-8 text-xs"
                                            >
                                              Abbrechen
                                            </Button>
                                          </div>
                                        </div>
                                      ) : inv.bank_details?.iban ? (
                                        <div className="space-y-1 text-xs">
                                          <div><span className="font-medium">Kontoinhaber:</span> {inv.bank_details.account_holder}</div>
                                          <div><span className="font-medium">IBAN:</span> {inv.bank_details.iban}</div>
                                          {inv.bank_details.bic && <div><span className="font-medium">BIC:</span> {inv.bank_details.bic}</div>}
                                          {inv.bank_details.bank_name && <div><span className="font-medium">Bank:</span> {inv.bank_details.bank_name}</div>}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-slate-600">
                                          Keine Bankverbindung hinterlegt. Bitte fügen Sie Ihre Kontodaten hinzu, um Auszahlungen zu erhalten.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="cancelled">
                  <div className="card">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Stornierte Investitionen</h2>
                    {cancelledInvestments.length === 0 ? (
                      <p className="text-slate-600">Sie haben keine stornierten Investitionen.</p>
                    ) : (
                      <div className="space-y-4">
                        {cancelledInvestments.map((investment) => (
                          <div key={investment.id} className="border border-red-200 rounded-lg overflow-hidden bg-red-50">
                            <div className="p-4">
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    {investment.project_image && (
                                      <img 
                                        src={investment.project_image} 
                                        alt={investment.project_title} 
                                        className="w-16 h-16 rounded-lg object-cover"
                                      />
                                    )}
                                    <div>
                                      <h3 className="text-lg font-bold text-slate-900">{investment.project_title}</h3>
                                      <p className="text-sm text-slate-500">
                                        Investitionsnummer: {investment.investment_number || 'N/A'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                  Storniert
                                </div>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                                <div>
                                  <p className="text-xs text-slate-500">Anteile</p>
                                  <p className="text-sm font-semibold text-slate-900">{investment.shares}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Investitionsbetrag</p>
                                  <p className="text-sm font-semibold text-slate-900">
                                    €{investment.total_amount?.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Preis/Anteil</p>
                                  <p className="text-sm font-semibold text-slate-900">
                                    €{investment.price_per_share?.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500">Stornierungsdatum</p>
                                  <p className="text-sm font-semibold text-slate-900">
                                    {investment.cancelled_at ? new Date(investment.cancelled_at).toLocaleDateString('de-DE') : 'N/A'}
                                  </p>
                                </div>
                              </div>

                              {investment.cancellation_reason && (
                                <div className="mt-3 pt-3 border-t border-red-300">
                                  <p className="text-xs text-slate-500 mb-1">Stornierungsgrund:</p>
                                  <p className="text-sm text-slate-900">{investment.cancellation_reason}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="profits">
                  <div className="card">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Ertragsübersicht</h2>
                    {groupedProfits.length === 0 ? (
                      <p className="text-slate-600">Noch keine Erträge verzeichnet.</p>
                    ) : (
                      <div className="space-y-6">
                        {/* Project-based profit cards with circle charts */}
                        {groupedProfits.map((projectProfit) => (
                          <div key={projectProfit.project_id} className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
                            <div className="p-6">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Left: Circle Chart */}
                                <div className="flex items-center justify-center">
                                  <div className="w-48 h-48">
                                    <ProfitCircleChart 
                                      invested={projectProfit.total_invested} 
                                      profit={projectProfit.total_profit} 
                                    />
                                  </div>
                                </div>
                                
                                {/* Middle: Project Info */}
                                <div className="md:col-span-2 flex flex-col justify-center">
                                  <div className="flex items-center gap-4 mb-4">
                                    {projectProfit.project_image && (
                                      <img src={projectProfit.project_image} alt={projectProfit.project_title} className="w-16 h-16 rounded-lg object-cover" />
                                    )}
                                    <div>
                                      <h3 className="text-xl font-bold text-slate-900">{projectProfit.project_title}</h3>
                                      <p className="text-sm text-slate-600">{projectProfit.total_shares} Anteile</p>
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-3 gap-4">
                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                      <p className="text-xs text-blue-700 mb-1">Investiert</p>
                                      <p className="text-lg font-bold text-blue-900">€{projectProfit.total_invested.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                      <p className="text-xs text-green-700 mb-1">Ertrag</p>
                                      <p className="text-lg font-bold text-green-900">€{projectProfit.total_profit.toLocaleString()}</p>
                                    </div>
                                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                      <p className="text-xs text-purple-700 mb-1">Rendite</p>
                                      <p className="text-lg font-bold text-purple-900">{projectProfit.return_percentage?.toFixed(1)}%</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Profit History by Year */}
                              {Object.keys(projectProfit.profits_by_year || {}).length > 0 && (
                                <div className="mt-6 border-t pt-4">
                                  <h4 className="font-semibold text-slate-700 mb-3">Ertraghistorie</h4>
                                  <div className="space-y-2">
                                    {Object.values(projectProfit.profits_by_year).map((yearData) => {
                                      const yearKey = `${projectProfit.project_id}-${yearData.year}`;
                                      return (
                                        <div key={yearData.year} className="border border-slate-200 rounded-lg overflow-hidden">
                                          {/* Year Header */}
                                          <div 
                                            className="p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-all flex justify-between items-center"
                                            onClick={() => toggleYearDetails(yearKey)}
                                          >
                                            <div className="flex items-center gap-3">
                                              <span className="text-lg font-bold text-slate-700">{yearData.year}</span>
                                              <span className="text-sm text-slate-500">{yearData.months?.length || 0} {yearData.months?.length === 1 ? 'Monat' : 'Monate'}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                              <span className="text-lg font-bold text-green-600">€{yearData.total.toLocaleString()}</span>
                                              <span className="text-slate-400 text-xl">{expandedYears[yearKey] ? '▼' : '▶'}</span>
                                            </div>
                                          </div>
                                          
                                          {/* Monthly Details */}
                                          {expandedYears[yearKey] && (
                                            <div className="p-3 bg-white space-y-3">
                                              {yearData.months?.map((monthData, idx) => {
                                                // Calculate project info for this profit
                                                const project = projects.find(p => p.id === projectProfit.project_id);
                                                const totalProjectShares = project?.total_shares || 1;
                                                const userSharePercentage = (projectProfit.total_shares / totalProjectShares) * 100;
                                                
                                                return (
                                                  <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                                                    {/* Main profit display */}
                                                    <div className="flex justify-between items-center p-3 bg-slate-50">
                                                      <span className="text-sm font-medium text-slate-700">{formatMonthYear(monthData.month)}</span>
                                                      <span className="text-sm font-bold text-green-600">€{monthData.amount.toLocaleString()}</span>
                                                    </div>
                                                    
                                                    {/* Calculation breakdown */}
                                                    <div className="p-3 bg-blue-50 border-t border-blue-200">
                                                      <div className="text-xs text-slate-600 space-y-1">
                                                        <div className="flex justify-between items-center">
                                                          <span className="text-slate-500">Ihre Anteile am Projekt:</span>
                                                          <span className="font-semibold">{projectProfit.total_shares} / {totalProjectShares} Anteile</span>
                                                        </div>
                                                        <div className="flex justify-between items-center">
                                                          <span className="text-slate-500">Ihr Anteil am Gesamtvolumen:</span>
                                                          <span className="font-semibold text-blue-700">{userSharePercentage.toFixed(2)}%</span>
                                                        </div>
                                                        <div className="pt-2 border-t border-blue-300 flex justify-between items-center">
                                                          <span className="text-slate-700 font-medium">Berechnung:</span>
                                                          <span className="font-mono text-xs text-slate-600">
                                                            ({projectProfit.total_shares} ÷ {totalProjectShares}) × Ertrag
                                                          </span>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Bank Accounts Section - Investment-specific */}
                    <div className="mt-10 pt-8 border-t border-slate-200">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4">Auszahlungskonten</h2>
                      <p className="text-sm text-slate-600 mb-6">
                        Ihre hinterlegten Bankverbindungen für Ertrag-Auszahlungen je Investition.
                      </p>

                      {groupedProfits.map((projectProfit) => {
                        // Find investments for this project
                        const projectInvestments = investments.filter(inv => inv.project_id === projectProfit.project_id);
                        
                        return (
                          <div key={projectProfit.project_id} className="mb-6 border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4">
                              <div className="flex items-center gap-3">
                                {projectProfit.project_image && (
                                  <img 
                                    src={projectProfit.project_image} 
                                    alt={projectProfit.project_title}
                                    className="w-12 h-12 rounded-lg object-cover"
                                  />
                                )}
                                <h3 className="text-lg font-bold text-slate-900">{projectProfit.project_title}</h3>
                              </div>
                            </div>
                            
                            <div className="p-4 space-y-4">
                              {projectInvestments.length === 0 ? (
                                <p className="text-sm text-slate-500">Keine Investitionen in diesem Projekt</p>
                              ) : projectInvestments.map((inv) => (
                                <div key={inv.id} className="border border-green-200 rounded-lg p-4 bg-green-50">
                                  <div className="flex items-start justify-between mb-2">
                                    <div>
                                      <p className="text-sm font-semibold text-slate-900">
                                        Investition: {inv.shares} Anteile
                                      </p>
                                      <p className="text-xs text-slate-600">
                                        Investitionsnummer: {inv.investment_number}
                                      </p>
                                    </div>
                                    {editingInvestmentBank === inv.id ? null : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditInvestmentBank(inv)}
                                        className="h-7 px-2 text-xs"
                                      >
                                        <Edit className="w-3 h-3 mr-1" />
                                        {inv.bank_details?.iban ? 'Bearbeiten' : 'Hinzufügen'}
                                      </Button>
                                    )}
                                  </div>
                                  
                                  {editingInvestmentBank === inv.id ? (
                                    <div className="space-y-2 mt-3">
                                      <div>
                                        <Label className="text-xs">Kontoinhaber*</Label>
                                        <Input
                                          value={investmentBankForm.account_holder}
                                          onChange={(e) => setInvestmentBankForm({...investmentBankForm, account_holder: e.target.value})}
                                          placeholder="Max Mustermann"
                                          className="h-8 text-xs"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">IBAN*</Label>
                                        <Input
                                          value={investmentBankForm.iban}
                                          onChange={(e) => setInvestmentBankForm({...investmentBankForm, iban: e.target.value})}
                                          placeholder="DE89 3704 0044 0532 0130 00"
                                          className="h-8 text-xs"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">BIC (optional)</Label>
                                        <Input
                                          value={investmentBankForm.bic}
                                          onChange={(e) => setInvestmentBankForm({...investmentBankForm, bic: e.target.value})}
                                          placeholder="COBADEFFXXX"
                                          className="h-8 text-xs"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Bank (optional)</Label>
                                        <Input
                                          value={investmentBankForm.bank_name}
                                          onChange={(e) => setInvestmentBankForm({...investmentBankForm, bank_name: e.target.value})}
                                          placeholder="Commerzbank"
                                          className="h-8 text-xs"
                                        />
                                      </div>
                                      <div className="flex gap-2 mt-2">
                                        <Button
                                          size="sm"
                                          onClick={handleSaveInvestmentBankDetails}
                                          className="flex-1 h-8 text-xs bg-green-600 hover:bg-green-700"
                                        >
                                          Speichern
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setEditingInvestmentBank(null);
                                            setInvestmentBankForm({ iban: '', bic: '', account_holder: '', bank_name: '' });
                                          }}
                                          className="h-8 text-xs"
                                        >
                                          Abbrechen
                                        </Button>
                                      </div>
                                    </div>
                                  ) : inv.bank_details?.iban ? (
                                    <div className="mt-3 space-y-1 text-xs bg-white p-3 rounded border border-green-300">
                                      <div><span className="font-medium">Kontoinhaber:</span> {inv.bank_details.account_holder}</div>
                                      <div><span className="font-medium">IBAN:</span> {inv.bank_details.iban}</div>
                                      {inv.bank_details.bic && <div><span className="font-medium">BIC:</span> {inv.bank_details.bic}</div>}
                                      {inv.bank_details.bank_name && <div><span className="font-medium">Bank:</span> {inv.bank_details.bank_name}</div>}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-600 mt-2">
                                      ⚠️ Keine Bankverbindung hinterlegt. Bitte fügen Sie Ihre Kontodaten hinzu.
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Old Bank Accounts Section - Keep for backward compatibility */}
                    <div className="hidden">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4">Auszahlungskonten (Alt)</h2>
                      <p className="text-sm text-slate-600 mb-6">
                        Hinterlegen Sie Ihre Bankverbindung für die Auszahlung der Erträge je Projekt.
                      </p>

                      {groupedProfits.map((projectProfit) => {
                        const existingAccount = userBankAccounts.find(acc => acc.project_id === projectProfit.project_id);
                        const isEditing = editingBankAccount?.project_id === projectProfit.project_id;

                        return (
                          <div key={projectProfit.project_id} className="mb-6 border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                {projectProfit.project_image && (
                                  <img 
                                    src={projectProfit.project_image} 
                                    alt={projectProfit.project_title}
                                    className="w-12 h-12 rounded-lg object-cover"
                                  />
                                )}
                                <h3 className="text-lg font-bold text-slate-900">{projectProfit.project_title}</h3>
                              </div>
                              {!isEditing && (
                                <Button
                                  size="sm"
                                  onClick={() => startEditingBankAccount(projectProfit.project_id, projectProfit.project_title)}
                                  variant={existingAccount ? "outline" : "default"}
                                >
                                  {existingAccount ? 'Bearbeiten' : 'Hinzufügen'}
                                </Button>
                              )}
                            </div>

                            <div className="p-4 bg-white">
                              {!isEditing && existingAccount ? (
                                <div>
                                  {/* Status Badge */}
                                  <div className="mb-3">
                                    {existingAccount.status === 'approved' && (
                                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                                        ✓ Genehmigt
                                      </span>
                                    )}
                                    {existingAccount.status === 'pending' && (
                                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                                        ⏳ Wartet auf Genehmigung
                                      </span>
                                    )}
                                    {existingAccount.status === 'rejected' && (
                                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                        ✗ Abgelehnt
                                      </span>
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-sm text-slate-500">Kontoinhaber:</span>
                                      <span className="font-medium text-slate-900">{existingAccount.account_holder}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-sm text-slate-500">IBAN:</span>
                                      <span className="font-mono font-medium text-slate-900 text-sm">{existingAccount.iban}</span>
                                    </div>
                                    {existingAccount.bic && (
                                      <div className="flex justify-between">
                                        <span className="text-sm text-slate-500">BIC:</span>
                                        <span className="font-medium text-slate-900">{existingAccount.bic}</span>
                                      </div>
                                    )}
                                    {existingAccount.bank_name && (
                                      <div className="flex justify-between">
                                        <span className="text-sm text-slate-500">Bank:</span>
                                        <span className="font-medium text-slate-900">{existingAccount.bank_name}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : !isEditing ? (
                                <p className="text-sm text-slate-500 italic">Noch kein Auszahlungskonto hinterlegt</p>
                              ) : (
                                <form onSubmit={handleSaveBankAccount} className="space-y-4">
                                  <div>
                                    <Label>Kontoinhaber *</Label>
                                    <Input
                                      value={bankAccountForm.account_holder}
                                      onChange={(e) => setBankAccountForm({...bankAccountForm, account_holder: e.target.value})}
                                      required
                                      placeholder="Max Mustermann"
                                    />
                                  </div>
                                  <div>
                                    <Label>IBAN *</Label>
                                    <Input
                                      value={bankAccountForm.iban}
                                      onChange={(e) => setBankAccountForm({...bankAccountForm, iban: e.target.value})}
                                      required
                                      placeholder="DE89370400440532013000"
                                      className="font-mono"
                                    />
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <Label>BIC</Label>
                                      <Input
                                        value={bankAccountForm.bic}
                                        onChange={(e) => setBankAccountForm({...bankAccountForm, bic: e.target.value})}
                                        placeholder="COBADEFFXXX"
                                      />
                                    </div>
                                    <div>
                                      <Label>Bank</Label>
                                      <Input
                                        value={bankAccountForm.bank_name}
                                        onChange={(e) => setBankAccountForm({...bankAccountForm, bank_name: e.target.value})}
                                        placeholder="Commerzbank"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex justify-end gap-2 pt-2">
                                    <Button type="button" variant="outline" onClick={cancelEditingBankAccount}>
                                      Abbrechen
                                    </Button>
                                    <Button type="submit" className="bg-green-600 hover:bg-green-700">
                                      Speichern
                                    </Button>
                                  </div>
                                </form>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="interests">
                  <div className="card">
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">Meine Interessen</h2>
                    {interests.length === 0 ? (
                      <p className="text-slate-600">Sie haben noch kein Interesse bekundet.</p>
                    ) : (
                      <div className="space-y-4">
                        {interests.map((interest, index) => {
                          const project = projects.find(p => p.id === interest.project_id);
                          
                          return (
                            <div key={interest.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid={`interest-${index}`}>
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3">
                                    {project?.image_url && (
                                      <img 
                                        src={project.image_url} 
                                        alt={project.title}
                                        className="w-16 h-16 rounded-lg object-cover"
                                      />
                                    )}
                                    <div>
                                      <p className="font-semibold text-slate-900">{project?.title || `Projekt: ${interest.project_id}`}</p>
                                      <p className="text-sm text-slate-600">
                                        Interesse bekundet am: {new Date(interest.created_at).toLocaleDateString('de-DE')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                    interest.status === 'approved' ? 'bg-green-100 text-green-800 border border-green-200' : 
                                    interest.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : 
                                    'bg-red-100 text-red-800 border border-red-200'
                                  }`}>
                                    {interest.status === 'approved' ? '✓ Genehmigt' : interest.status === 'pending' ? '⏳ Ausstehend' : '✗ Abgelehnt'}
                                  </span>
                                  {interest.status === 'approved' && (
                                    project?.is_visible ? (
                                      <Button
                                        size="sm"
                                        onClick={() => navigate(`/bess/projects/${interest.project_id}`)}
                                        className="bg-blue-600 hover:bg-blue-700"
                                      >
                                        Zum Projekt →
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="bg-amber-50 text-amber-700 border-amber-300 cursor-default"
                                        title="Dieses Projekt ist derzeit nicht verfügbar"
                                      >
                                        ⓘ Update
                                      </Button>
                                    )
                                  )}
                                  {interest.status === 'rejected' && (
                                    <Button
                                      size="sm"
                                      onClick={async () => {
                                        try {
                                          // Resubmit interest by calling the same API
                                          const response = await axios.post(`${API}/interest`, {
                                            project_id: interest.project_id,
                                            email: user.email,
                                            phone: user.phone || '',
                                            name: user.username
                                          }, {
                                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                          });
                                          
                                          toast.success('Interesse wurde erneut bekundet');
                                          
                                          // Refresh interests
                                          const updatedInterests = await axios.get(`${API}/interest`, {
                                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                          });
                                          setInterests(updatedInterests.data);
                                        } catch (error) {
                                          console.error('Error resubmitting interest:', error);
                                          toast.error('Fehler beim erneuten Bekunden des Interesses');
                                        }
                                      }}
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      Erneut Interesse bekunden
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {(user.role === 'admin' || user.role === 'finance' || user.role === 'content_filler' || user.role === 'superadmin') && (
            <div>
              {/* Export Button for Admin/Finance */}
              {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && (
                <div className="mb-6">
                  <Button
                    onClick={() => setShowExportModal(true)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    <FileDown size={20} className="mr-2" />
                    Bericht exportieren
                  </Button>
                </div>
              )}

              {/* Admin/SuperAdmin Quick Links */}
              {(user.role === 'admin' || user.role === 'superadmin') && (
                <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/bess/admin/land-offers" data-testid="admin-land-offers-link">
                    <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-orange-50 to-amber-100">
                      <div className="flex items-center">
                        <MapPin size={32} className="text-orange-600 mr-4" />
                        <div>
                          <h3 className="font-bold text-slate-900">Flächenangebote</h3>
                          <p className="text-sm text-slate-600">Eingereichte Flächen prüfen</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <Link to="/bess/admin/users">
                    <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100">
                      <div className="flex items-center">
                        <Users size={32} className="text-blue-600 mr-4" />
                        <div>
                          <h3 className="font-bold text-slate-900">Benutzerverwaltung</h3>
                          <p className="text-sm text-slate-600">User verwalten</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <Link to="/bess/admin/settings">
                    <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-green-50 to-green-100">
                      <div className="flex items-center">
                        <Mail size={32} className="text-green-600 mr-4" />
                        <div>
                          <h3 className="font-bold text-slate-900">Einstellungen</h3>
                          <p className="text-sm text-slate-600">E-Mail & System</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <Link to="/bess/admin/content">
                    <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-purple-50 to-purple-100">
                      <div className="flex items-center">
                        <svg className="text-purple-600 mr-4" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <div>
                          <h3 className="font-bold text-slate-900">Content Management</h3>
                          <p className="text-sm text-slate-600">Seiten-Texte</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  {user.role === 'superadmin' && (
                    <Link to="/bess/admin/database">
                      <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200">
                        <div className="flex items-center">
                          <svg className="text-red-600 mr-4" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                          </svg>
                          <div>
                            <h3 className="font-bold text-slate-900">Datenbankverwaltung</h3>
                            <p className="text-sm text-red-600 font-semibold">⚠️ Nur SuperAdmin</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  )}
                  
                  {user.role === 'superadmin' && (
                    <Link to="/bess/superadmin/backup">
                      <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-purple-50 to-blue-100 border-2 border-purple-200">
                        <div className="flex items-center">
                          <svg className="text-purple-600 mr-4" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                          </svg>
                          <div>
                            <h3 className="font-bold text-slate-900">Datenbank-Backup</h3>
                            <p className="text-sm text-purple-600 font-semibold">💾 Sicherung & Wiederherstellung</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  )}
                  
                  {user.role === 'superadmin' && (
                    <Link to="/bess/superadmin/recaptcha-settings">
                      <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200">
                        <div className="flex items-center">
                          <svg className="text-green-600 mr-4" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <div>
                            <h3 className="font-bold text-slate-900">reCAPTCHA Einstellungen</h3>
                            <p className="text-sm text-green-600 font-semibold">🛡️ Spam-Schutz verwalten</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  )}
                  
                  {user.role === 'superadmin' && (
                    <Link to="/bess/superadmin/direct-investment">
                      <div className="card hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-orange-50 to-amber-100 border-2 border-orange-200">
                        <div className="flex items-center">
                          <svg className="text-orange-600 mr-4" width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <h3 className="font-bold text-slate-900">Direkte Investition</h3>
                            <p className="text-sm text-orange-600 font-semibold">💰 Investitionen direkt erstellen</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              )}

              <Tabs value={adminActiveTab} onValueChange={setAdminActiveTab} className="w-full">
                <TabsList className="mb-6">
                  <TabsTrigger value="projects" data-testid="admin-tab-projects">Projekte</TabsTrigger>
                  {(user.role === 'admin' || user.role === 'superadmin') && <TabsTrigger value="interests" data-testid="admin-tab-interests">Interessen verwalten</TabsTrigger>}
                  {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && <TabsTrigger value="investments" data-testid="admin-tab-investments">Alle Investitionen</TabsTrigger>}
                  {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && <TabsTrigger value="payments" data-testid="admin-tab-payments">Zahlungsverwaltung</TabsTrigger>}
                  {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && <TabsTrigger value="profit-management" data-testid="admin-tab-profit-management">Erträge verwalten</TabsTrigger>}
                  {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && <TabsTrigger value="bank-accounts" data-testid="admin-tab-bank-accounts">Bankdaten-Anfragen</TabsTrigger>}
                  {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && <TabsTrigger value="cancellations" data-testid="admin-tab-cancellations">Stornierungsanfragen</TabsTrigger>}
                </TabsList>

                <TabsContent value="projects">
                  <div className="card">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-2xl font-bold text-slate-900">Projekte verwalten</h2>
                      <Link to="/bess/projects/new/edit">
                        <Button className="bg-gradient-to-r from-green-500 to-blue-600 text-white" data-testid="add-project-btn">
                          <Plus size={20} className="mr-2" />
                          Neues Projekt
                        </Button>
                      </Link>
                    </div>
                    <div className="space-y-4">
                      {projects.map((project, index) => {
                        const soldShares = project.total_shares - project.available_shares;
                        const soldPercentage = ((soldShares / project.total_shares) * 100).toFixed(1);
                        const projectInvestors = user.role === 'admin' ? getProjectInvestors(project.id) : [];
                        const totalInvested = projectInvestors.reduce((sum, inv) => sum + inv.total_amount, 0);
                        
                        return (
                          <div key={project.id} className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden" data-testid={`admin-project-${index}`}>
                            <div className="p-4">
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex-1">
                                  <p className="font-semibold text-slate-900">{project.title}</p>
                                  <p className="text-sm text-slate-600">{project.location} - {project.capacity_mw} MW</p>
                                  <p className="text-sm text-slate-600">
                                    Verfügbar: <strong>{project.available_shares}</strong>/{project.total_shares} Anteile
                                    <span className="text-green-600 ml-2">({soldShares} verkauft - {soldPercentage}%)</span>
                                  </p>
                                  {user.role === 'admin' && projectInvestors.length > 0 && (
                                    <p className="text-sm text-blue-600 mt-1">
                                      💰 Gesamt investiert: <strong>€{totalInvested.toLocaleString()}</strong>
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-3">
                                  <span className={`badge ${project.status === 'active' ? 'badge-green' : 'badge-blue'}`}>
                                    {project.status}
                                  </span>
                                  <Link to={`/bess/projects/${project.id}/edit`}>
                                    <Button variant="outline" size="sm" data-testid={`edit-project-btn-${index}`}>
                                      Bearbeiten
                                    </Button>
                                  </Link>
                                </div>
                              </div>
                              <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                                <div
                                  className="bg-gradient-to-r from-green-500 to-blue-600 h-2 rounded-full"
                                  style={{ width: `${((project.total_shares - project.available_shares) / project.total_shares) * 100}%` }}
                                />
                              </div>
                              
                              {/* Investors Toggle Button */}
                              {user.role === 'admin' && projectInvestors.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleProjectInvestors(project.id)}
                                  className="w-full mt-3 text-slate-600 hover:text-slate-900"
                                  data-testid={`toggle-investors-${index}`}
                                >
                                  {expandedProjects[project.id] ? '▼' : '▶'} {projectInvestors.length} Investor(en) anzeigen
                                </Button>
                              )}
                            </div>
                            
                            {/* Investors List */}
                            {user.role === 'admin' && expandedProjects[project.id] && projectInvestors.length > 0 && (
                              <div className="border-t border-slate-200 bg-white p-4">
                                <h4 className="font-semibold text-slate-900 mb-3">Investoren-Übersicht</h4>
                                <div className="space-y-3">
                                  {projectInvestors.map((inv, invIndex) => {
                                    const paymentPercentage = inv.total_amount > 0 ? (inv.paid_amount / inv.total_amount) * 100 : 0;
                                    const paymentStages = project.payment_stages || [50, 30, 15, 5];
                                    
                                    // Calculate cumulative percentages for stages
                                    const cumulativeStages = [];
                                    let cumulative = 0;
                                    paymentStages.forEach(stage => {
                                      cumulative += stage;
                                      cumulativeStages.push(cumulative);
                                    });
                                    
                                    const handleInvestmentClick = () => {
                                      // Switch to Zahlungsverwaltung tab using state
                                      setAdminActiveTab('payments');
                                      
                                      // Wait a bit before highlighting to ensure tab has switched
                                      setTimeout(() => {
                                        setHighlightedInvestmentId(inv.id);
                                        
                                        // Scroll to investment row
                                        setTimeout(() => {
                                          const investmentRow = document.querySelector(`[data-investment-id="${inv.id}"]`);
                                          if (investmentRow) {
                                            investmentRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          }
                                        }, 200);
                                      }, 100);
                                      
                                      // Clear highlight after 3 seconds
                                      setTimeout(() => {
                                        setHighlightedInvestmentId(null);
                                      }, 3000);
                                    };
                                    
                                    return (
                                      <div key={inv.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`investor-${index}-${invIndex}`}>
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <p className="font-semibold text-slate-900">{inv.username}</p>
                                              <button
                                                onClick={handleInvestmentClick}
                                                className="text-xs font-mono font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors"
                                                title="Zur Zahlungsverwaltung springen"
                                              >
                                                {inv.investment_number}
                                              </button>
                                            </div>
                                            <p className="text-sm text-slate-600">{inv.email}</p>
                                            <p className="text-xs text-slate-500">
                                              Investiert am: {new Date(inv.created_at).toLocaleDateString('de-DE')}
                                            </p>
                                          </div>
                                          <div className="text-right">
                                            <p className="text-lg font-bold text-green-600">€{inv.total_amount.toLocaleString()}</p>
                                            <p className="text-sm text-slate-600">{inv.shares} Anteile</p>
                                            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold mt-1 ${
                                              inv.payment_status === 'bezahlt' ? 'bg-green-100 text-green-800' :
                                              inv.payment_status === 'teilweise_bezahlt' ? 'bg-yellow-100 text-yellow-800' :
                                              'bg-slate-100 text-slate-800'
                                            }`}>
                                              {inv.payment_status === 'bezahlt' ? 'Bezahlt' :
                                               inv.payment_status === 'teilweise_bezahlt' ? 'Teilweise bezahlt' :
                                               'Offen'}
                                            </span>
                                          </div>
                                        </div>
                                        
                                        {/* Payment Progress Bar - Thinner */}
                                        <div className="mt-2">
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-semibold text-slate-600">Zahlungsfortschritt</span>
                                            <span className="text-xs font-semibold text-slate-600">
                                              €{(inv.paid_amount || 0).toLocaleString()} / €{inv.total_amount.toLocaleString()} ({paymentPercentage.toFixed(1)}%)
                                            </span>
                                          </div>
                                          
                                          {/* Progress Bar with Stages - Thinner (h-5 instead of h-8) */}
                                          <div className="relative w-full h-5 bg-slate-200 rounded-lg overflow-hidden">
                                            {/* Paid Amount Bar */}
                                            <div 
                                              className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300"
                                              style={{ width: `${Math.min(paymentPercentage, 100)}%` }}
                                            />
                                            
                                            {/* Stage Markers - Smaller */}
                                            {cumulativeStages.map((stagePercentage, idx) => {
                                              const isReached = paymentPercentage >= stagePercentage;
                                              return (
                                                <div 
                                                  key={idx}
                                                  className="absolute top-0 h-full border-r border-white flex items-center justify-center"
                                                  style={{ left: `${stagePercentage}%` }}
                                                >
                                                  <div className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold ${
                                                    isReached ? 'bg-green-600 text-white' : 'bg-slate-300 text-slate-600'
                                                  }`}>
                                                    {idx + 1}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          
                                          {/* Stage Labels - Smaller text */}
                                          <div className="flex justify-between mt-1 text-[10px] text-slate-600">
                                            {paymentStages.map((stage, idx) => {
                                              const isReached = paymentPercentage >= cumulativeStages[idx];
                                              return (
                                                <span key={idx} className={`font-semibold ${isReached ? 'text-green-600' : 'text-slate-500'}`}>
                                                  S{idx + 1}: {stage}%
                                                </span>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>

                {user.role === 'admin' && (
                  <TabsContent value="interests">
                    <div className="card">
                      <h2 className="text-2xl font-bold text-slate-900 mb-6">Interessen genehmigen</h2>
                      {interests.filter(i => i.status === 'pending').length === 0 ? (
                        <p className="text-slate-600">Keine ausstehenden Interessen.</p>
                      ) : (
                        <div className="space-y-4">
                          {interests.filter(i => i.status === 'pending').map((interest, index) => (
                            <div key={interest.id} className="p-6 bg-slate-50 rounded-lg border border-slate-200" data-testid={`pending-interest-${index}`}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                  <p className="text-sm text-slate-500">Name</p>
                                  <p className="font-semibold text-slate-900">{interest.user_name}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-slate-500">E-Mail</p>
                                  <p className="font-semibold text-slate-900">{interest.user_email}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-slate-500">Telefon</p>
                                  <p className="font-semibold text-slate-900">{interest.user_phone}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-slate-500">Projekt</p>
                                  <p className="font-semibold text-slate-900">
                                    {interest.project_name ? (
                                      <>
                                        {interest.project_name}
                                        <span className="text-xs text-slate-500 block mt-1">ID: {interest.project_id}</span>
                                      </>
                                    ) : (
                                      `ID: ${interest.project_id}`
                                    )}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-slate-500">User ID</p>
                                  <p className="text-sm text-slate-600">{interest.user_id}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-slate-500">Datum</p>
                                  <p className="text-sm text-slate-600">{new Date(interest.created_at).toLocaleDateString('de-DE')}</p>
                                </div>
                              </div>
                              <div className="flex justify-end space-x-3">
                                <Button
                                  onClick={() => handleApproveInterest(interest.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`approve-interest-btn-${index}`}
                                >
                                  ✓ Genehmigen
                                </Button>
                                <Button
                                  onClick={() => handleRejectInterest(interest.id)}
                                  variant="outline"
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  data-testid={`reject-interest-btn-${index}`}
                                >
                                  ✗ Ablehnen
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}

                {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && (
                  <TabsContent value="investments">
                    <div className="card">
                      <div className="mb-6 flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-slate-900">Alle Investitionen</h2>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setInvestmentGrouping('none')}
                            variant={investmentGrouping === 'none' ? 'default' : 'outline'}
                            size="sm"
                          >
                            Liste
                          </Button>
                          <Button
                            onClick={() => setInvestmentGrouping('user')}
                            variant={investmentGrouping === 'user' ? 'default' : 'outline'}
                            size="sm"
                          >
                            Nach User
                          </Button>
                          <Button
                            onClick={() => setInvestmentGrouping('project')}
                            variant={investmentGrouping === 'project' ? 'default' : 'outline'}
                            size="sm"
                          >
                            Nach Projekt
                          </Button>
                        </div>
                      </div>

                      {/* Ungrouped List View */}
                      {investmentGrouping === 'none' && (
                        <div className="space-y-4">
                          {investments.map((inv, index) => {
                            const investmentUser = users.find(u => u.id === inv.user_id);
                            const userName = investmentUser ? investmentUser.username : 'Unbekannt';
                            const userEmail = investmentUser ? investmentUser.email : '';
                            const project = projects.find(p => p.id === inv.project_id);
                            const isCancelled = inv.cancellation_status === 'approved' || inv.status === 'cancelled' || inv.payment_status === 'storniert';
                            const cardClass = isCancelled ? "p-4 bg-red-100 rounded-lg border border-red-300" : "p-4 bg-slate-50 rounded-lg border border-slate-200";
                            
                            return (
                              <div key={inv.id} className={cardClass} data-testid={`admin-investment-${index}`}>
                                <div className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <p className="font-semibold text-slate-900">{userName}</p>
                                    <p className="text-xs text-slate-500 mb-1">{userEmail}</p>
                                    <p className="text-sm text-slate-600">Projekt: {project?.title || inv.project_id}</p>
                                    <p className="text-sm text-slate-600 mb-2">Anteile: {inv.shares} - Nummer: {inv.investment_number}</p>
                                    {getFullInvestmentStatus(inv)}
                                    {isCancelled && (
                                      <div className="mt-2 flex items-center gap-2">
                                        <input type="checkbox" checked={true} readOnly className="w-4 h-4 text-red-600" />
                                        <span className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded font-semibold">
                                          Storniert {inv.payment_status === 'storniert' ? '(Administrator)' : '(Kunde)'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right flex items-center gap-3">
                                    <div>
                                      <p className="text-lg font-bold text-green-600">€{inv.total_amount.toLocaleString()}</p>
                                      <p className="text-xs text-slate-500">{new Date(inv.created_at).toLocaleDateString('de-DE')}</p>
                                    </div>
                                    {(inv.payment_status === 'offen' || !inv.payment_status) && (
                                      <button
                                        onClick={() => handleDeleteInvestment(inv.id)}
                                        className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded transition-all"
                                        title="Investition löschen"
                                      >
                                        <Trash2 size={18} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Grouped by User */}
                      {investmentGrouping === 'user' && (
                        <div className="space-y-4">
                          {groupInvestmentsByUser().map((group) => (
                            <div key={group.user_id} className="border border-slate-200 rounded-lg overflow-hidden">
                              <div 
                                className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 cursor-pointer hover:from-blue-100 hover:to-blue-200 transition-all"
                                onClick={() => toggleProjectInvestors(group.user_id)}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-bold text-slate-900 text-lg">{group.user_name}</p>
                                    <p className="text-sm text-slate-600">{group.user_email}</p>
                                    <p className="text-sm text-slate-600 mt-1">
                                      {group.investments.length} {group.investments.length === 1 ? 'Investition' : 'Investitionen'} • {group.total_shares} Anteile
                                    </p>
                                    <p className="text-sm font-semibold text-green-700 mt-1">
                                      ✓ {group.profit_eligible_shares} gewinnberechtigte Anteile
                                    </p>
                                  </div>
                                  <div className="text-right flex items-center gap-3">
                                    <div>
                                      <p className="text-xs text-slate-500 mb-1">Gesamtsumme</p>
                                      <p className="text-2xl font-bold text-green-600">€{group.total_amount.toLocaleString()}</p>
                                    </div>
                                    <span className="text-slate-400 text-2xl">{expandedProjects[group.user_id] ? '▼' : '▶'}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {expandedProjects[group.user_id] && (
                                <div className="bg-white p-4 space-y-2">
                                  {group.investments.map((inv, idx) => {
                                    const project = projects.find(p => p.id === inv.project_id);
                                    const isCancelled = inv.cancellation_status === 'approved' || inv.status === 'cancelled' || inv.payment_status === 'storniert';
                                    const cardClass = isCancelled ? "p-3 bg-red-100 rounded border border-red-300" : "p-3 bg-slate-50 rounded border border-slate-200";
                                    return (
                                      <div key={inv.id} className={cardClass}>
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1">
                                            <p className="text-sm font-semibold text-slate-900">{project?.title || inv.project_id}</p>
                                            <p className="text-xs text-slate-600 mt-1 mb-2">
                                              {inv.investment_number} • {inv.shares} Anteile
                                            </p>
                                            {getFullInvestmentStatus(inv)}
                                            {isCancelled && (
                                              <div className="mt-2 flex items-center gap-2">
                                                <input type="checkbox" checked={true} readOnly className="w-4 h-4 text-red-600" />
                                                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-semibold">
                                                  Storniert {inv.payment_status === 'storniert' ? '(Administrator)' : '(Kunde)'}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          <div className="text-right flex items-center gap-2">
                                            <div>
                                              <p className="text-sm font-bold text-green-600">€{inv.total_amount.toLocaleString()}</p>
                                              <p className="text-xs text-slate-500">{new Date(inv.created_at).toLocaleDateString('de-DE')}</p>
                                            </div>
                                            {(inv.payment_status === 'offen' || !inv.payment_status) && (
                                              <button
                                                onClick={() => handleDeleteInvestment(inv.id)}
                                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded transition-all"
                                                title="Investition löschen"
                                              >
                                                <Trash2 size={16} />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Grouped by Project */}
                      {investmentGrouping === 'project' && (
                        <div className="space-y-4">
                          {groupInvestmentsByProject().map((group) => (
                            <div key={group.project_id} className="border border-slate-200 rounded-lg overflow-hidden">
                              <div 
                                className="p-4 bg-gradient-to-r from-green-50 to-green-100 cursor-pointer hover:from-green-100 hover:to-green-200 transition-all"
                                onClick={() => toggleProjectInvestors(group.project_id)}
                              >
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-bold text-slate-900 text-lg">{group.project_title}</p>
                                    <p className="text-sm text-slate-600 mt-1">
                                      {group.investments.length} {group.investments.length === 1 ? 'Investition' : 'Investitionen'} • {group.total_shares} Anteile
                                    </p>
                                  </div>
                                  <div className="text-right flex items-center gap-3">
                                    <div>
                                      <p className="text-xs text-slate-500 mb-1">Gesamtsumme</p>
                                      <p className="text-2xl font-bold text-green-600">€{group.total_amount.toLocaleString()}</p>
                                    </div>
                                    <span className="text-slate-400 text-2xl">{expandedProjects[group.project_id] ? '▼' : '▶'}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {expandedProjects[group.project_id] && (
                                <div className="bg-white p-4 space-y-2">
                                  {group.investments.map((inv, idx) => {
                                    const investmentUser = users.find(u => u.id === inv.user_id);
                                    const isCancelled = inv.cancellation_status === 'approved' || inv.status === 'cancelled' || inv.payment_status === 'storniert';
                                    const cardClass = isCancelled ? "p-3 bg-red-100 rounded border border-red-300" : "p-3 bg-slate-50 rounded border border-slate-200";
                                    return (
                                      <div key={inv.id} className={cardClass}>
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1">
                                            <p className="text-sm font-semibold text-slate-900">{investmentUser?.username || 'Unbekannt'}</p>
                                            <p className="text-xs text-slate-500">{investmentUser?.email || ''}</p>
                                            <p className="text-xs text-slate-600 mt-1 mb-2">
                                              {inv.investment_number} • {inv.shares} Anteile
                                            </p>
                                            {getFullInvestmentStatus(inv)}
                                            {isCancelled && (
                                              <div className="mt-2 flex items-center gap-2">
                                                <input type="checkbox" checked={true} readOnly className="w-4 h-4 text-red-600" />
                                                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-semibold">
                                                  Storniert {inv.payment_status === 'storniert' ? '(Administrator)' : '(Kunde)'}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                          <div className="text-right flex items-center gap-2">
                                            <div>
                                              <p className="text-sm font-bold text-green-600">€{inv.total_amount.toLocaleString()}</p>
                                              <p className="text-xs text-slate-500">{new Date(inv.created_at).toLocaleDateString('de-DE')}</p>
                                            </div>
                                            {(inv.payment_status === 'offen' || !inv.payment_status) && (
                                              <button
                                                onClick={() => handleDeleteInvestment(inv.id)}
                                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded transition-all"
                                                title="Investition löschen"
                                              >
                                                <Trash2 size={16} />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}

                {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && (
                  <TabsContent value="payments">
                    <div className="card">
                      <div className="mb-6">
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">Zahlungsverwaltung</h2>
                        <p className="text-sm text-slate-600">Verwalten Sie Zahlungseingänge und aktualisieren Sie den Status</p>
                        {(paymentStatusFilter || projectFilter) && (
                          <div className="mt-3 flex gap-2 flex-wrap">
                            {paymentStatusFilter && (
                              <div className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <span className="text-sm font-semibold text-blue-700">
                                  Status: {
                                    paymentStatusFilter === 'offen' ? 'Offene Zahlungen' :
                                    paymentStatusFilter === 'teilweise_bezahlt' ? 'Teilweise bezahlt' :
                                    'Bezahlt'
                                  }
                                </span>
                                <button
                                  onClick={() => setPaymentStatusFilter(null)}
                                  className="text-blue-700 hover:text-blue-900 font-bold text-lg"
                                  title="Filter entfernen"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                            {projectFilter && (
                              <div className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                                <span className="text-sm font-semibold text-purple-700">
                                  Projekt: {projects.find(p => p.id === projectFilter)?.title || 'Unbekannt'}
                                </span>
                                <button
                                  onClick={() => setProjectFilter(null)}
                                  className="text-purple-700 hover:text-purple-900 font-bold text-lg"
                                  title="Filter entfernen"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {investments.length === 0 ? (
                        <p className="text-slate-600">Keine Investitionen vorhanden.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-slate-200 bg-slate-50">
                                <th className="text-left p-3 text-sm font-semibold text-slate-700 w-32">Inv.-Nr.</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700">User</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700">Projekt</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700">Betrag</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700 w-16">Anteile</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700 w-24">Datum</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700">Zahlung</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700 w-20">Berechtigt</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700 w-24">Storno</th>
                                <th className="text-left p-3 text-sm font-semibold text-slate-700 w-32">Aktionen</th>
                              </tr>
                            </thead>
                            <tbody>
                              {investments
                                .filter(inv => {
                                  // Apply payment status filter if active
                                  if (paymentStatusFilter && inv.payment_status !== paymentStatusFilter) {
                                    return false;
                                  }
                                  // Apply project filter if active
                                  if (projectFilter && inv.project_id !== projectFilter) {
                                    return false;
                                  }
                                  return true;
                                })
                                .map((inv, index) => {
                                const investor = users.find(u => u.id === inv.user_id);
                                const project = projects.find(p => p.id === inv.project_id);
                                const isCancelled = inv.cancellation_status === 'approved' || inv.status === 'cancelled' || inv.payment_status === 'storniert';
                                const isHighlighted = highlightedInvestmentId === inv.id;
                                const rowClass = isCancelled 
                                  ? "border-b border-slate-100 hover:bg-red-50 bg-red-100" 
                                  : isHighlighted
                                    ? "border-b border-slate-100 bg-yellow-200 shadow-lg transition-all duration-500 animate-pulse"
                                    : "border-b border-slate-100 hover:bg-slate-50";
                                
                                return (
                                  <tr key={inv.id} className={rowClass} data-testid={`payment-row-${index}`} data-investment-id={inv.id}>
                                    <td className="p-3">
                                      <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded border border-slate-300">
                                        {inv.investment_number || `INV-${index + 1}`}
                                      </span>
                                    </td>
                                    <td className="p-3">
                                      <div>
                                        <p className="text-sm font-medium text-slate-900">{investor?.username || 'Unbekannt'}</p>
                                        <p className="text-xs text-slate-500">{investor?.email || 'N/A'}</p>
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      <button
                                        onClick={() => setProjectFilter(projectFilter === inv.project_id ? null : inv.project_id)}
                                        className={`text-sm text-left hover:underline transition-colors ${
                                          projectFilter === inv.project_id
                                            ? 'text-purple-700 font-semibold'
                                            : 'text-slate-900 hover:text-purple-600'
                                        }`}
                                        title="Nach diesem Projekt filtern"
                                      >
                                        {project?.title || inv.project_id}
                                      </button>
                                    </td>
                                    <td className="p-3">
                                      <div>
                                        <p className="text-sm font-bold text-slate-900">€{inv.total_amount.toLocaleString()}</p>
                                        {inv.paid_amount > 0 && inv.payment_status === 'teilweise_bezahlt' && (
                                          <>
                                            {(() => {
                                              const paidPercentage = (inv.paid_amount / inv.total_amount) * 100;
                                              const projectProgress = project?.project_progress || 0;
                                              const isProjectAhead = projectProgress > paidPercentage;
                                              
                                              return (
                                                <p className={`text-xs font-semibold ${isProjectAhead ? 'text-red-600' : 'text-green-600'}`}>
                                                  Bezahlt: €{inv.paid_amount.toLocaleString()} ({paidPercentage.toFixed(1)}%)
                                                </p>
                                              );
                                            })()}
                                            {(() => {
                                              // Calculate next stage amount
                                              const paymentStages = project?.payment_stages || [50, 30, 15, 5];
                                              const cumulativeStages = [];
                                              let cumulative = 0;
                                              paymentStages.forEach(stage => {
                                                cumulative += stage;
                                                cumulativeStages.push(cumulative);
                                              });
                                              
                                              const paidPercentage = (inv.paid_amount / inv.total_amount) * 100;
                                              const projectProgress = project?.project_progress || 0; // Baufortschritt
                                              
                                              // Find next stage
                                              let nextStageIndex = -1;
                                              for (let i = 0; i < cumulativeStages.length; i++) {
                                                if (paidPercentage < cumulativeStages[i]) {
                                                  nextStageIndex = i;
                                                  break;
                                                }
                                              }
                                              
                                              // Show warning if project progress is ahead of payment
                                              const shouldShowWarning = projectProgress > paidPercentage;
                                              
                                              if (nextStageIndex >= 0) {
                                                const nextStagePercentage = cumulativeStages[nextStageIndex];
                                                const nextStageAmount = (inv.total_amount * nextStagePercentage) / 100;
                                                const remainingAmount = nextStageAmount - inv.paid_amount;
                                                
                                                // Show remaining amount if project progress is ahead of payment
                                                // OR if the project has reached/exceeded the next stage
                                                if (remainingAmount > 0 && (shouldShowWarning || projectProgress >= nextStagePercentage)) {
                                                  return (
                                                    <div className="mt-1">
                                                      {shouldShowWarning && (
                                                        <p className="text-xs text-red-600 font-semibold mb-1">
                                                          ⚠️ Projekt bei {projectProgress}%, Zahlung nur {paidPercentage.toFixed(1)}%
                                                        </p>
                                                      )}
                                                      <p className={`text-xs font-semibold ${shouldShowWarning ? 'text-red-600' : 'text-orange-600'}`}>
                                                        Noch offen für Stufe {nextStageIndex + 1}: €{remainingAmount.toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                                                      </p>
                                                    </div>
                                                  );
                                                }
                                              }
                                              
                                              // Show warning even if no next stage (project ahead of payment)
                                              if (shouldShowWarning) {
                                                return (
                                                  <p className="text-xs text-red-600 font-semibold mt-1">
                                                    ⚠️ Projekt bei {projectProgress}%, Zahlung nur {paidPercentage.toFixed(1)}%
                                                  </p>
                                                );
                                              }
                                              
                                              return null;
                                            })()}
                                          </>
                                        )}
                                        {inv.payment_status === 'offen' && (() => {
                                          const projectProgress = project?.project_progress || 0;
                                          if (projectProgress > 0) {
                                            const recommendedAmount = (inv.total_amount * projectProgress) / 100;
                                            return (
                                              <p className="text-xs text-red-600 font-semibold mt-1">
                                                ⚠️ Projekt bei {projectProgress}% - Empfohlen: €{recommendedAmount.toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                                              </p>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      <p className="text-sm text-slate-700">{inv.shares}</p>
                                    </td>
                                    <td className="p-3">
                                      <p className="text-xs text-slate-500">{new Date(inv.created_at).toLocaleDateString('de-DE')}</p>
                                    </td>
                                    <td className="p-3">
                                      <div className="space-y-1">
                                        {getPaymentStatusBadge(inv.payment_status)}
                                        {inv.payment_confirmed_by_user && inv.payment_status === 'offen' && (
                                          <div className="flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded border border-yellow-300">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="font-semibold">User: Zahlung ausgeführt</span>
                                          </div>
                                        )}
                                        {inv.payment_confirmed_at && (
                                          <p className="text-xs text-slate-500">
                                            Bestätigt: {new Date(inv.payment_confirmed_at).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})}
                                          </p>
                                        )}
                                        
                                        {/* Payment Stages Status */}
                                        {(() => {
                                          const payment_stages_status = inv.payment_stages_status || {};
                                          const hasStages = Object.keys(payment_stages_status).length > 0;
                                          
                                          if (!hasStages) return null;
                                          
                                          return (
                                            <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                              <p className="text-xs font-semibold text-slate-700 mb-1">Zahlungsstufen:</p>
                                              <div className="space-y-1">
                                                {[1, 2, 3, 4].map(stage => {
                                                  const stage_status = payment_stages_status[stage.toString()] || {};
                                                  const user_confirmed = stage_status.user_confirmed;
                                                  const admin_confirmed = stage_status.admin_confirmed;
                                                  
                                                  if (!user_confirmed && !admin_confirmed) return null;
                                                  
                                                  return (
                                                    <div key={stage} className="flex items-center gap-2 text-xs">
                                                      {admin_confirmed ? (
                                                        <span className="text-green-600 font-semibold">✅ Stufe {stage}</span>
                                                      ) : user_confirmed ? (
                                                        <span className="text-yellow-600 font-semibold">⏱️ Stufe {stage}</span>
                                                      ) : null}
                                                      
                                                      {stage_status.admin_confirmed_at && (
                                                        <span className="text-slate-500">
                                                          {new Date(stage_status.admin_confirmed_at).toLocaleDateString('de-DE')}
                                                        </span>
                                                      )}
                                                      
                                                      {user_confirmed && !admin_confirmed && (
                                                        <div className="flex gap-1">
                                                          <Button
                                                            size="sm"
                                                            className="h-5 px-2 py-0 text-xs bg-green-600 hover:bg-green-700"
                                                            onClick={() => handleAdminConfirmStage(inv.id, stage)}
                                                          >
                                                            ✓ Vollständig
                                                          </Button>
                                                          <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-5 px-2 py-0 text-xs border-orange-500 text-orange-600 hover:bg-orange-50"
                                                            onClick={() => handleAdminPartialConfirmStage(inv.id, stage)}
                                                          >
                                                            ⚠ Teilweise
                                                          </Button>
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                    <td className="p-3 align-middle text-center">
                                      <input
                                        type="checkbox"
                                        checked={inv.profit_eligible || false}
                                        disabled={inv.payment_status !== 'bezahlt'}
                                        onChange={(e) => handleUpdateProfitEligible(inv.id, e.target.checked)}
                                        className="w-4 h-4 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                        title={inv.payment_status !== 'bezahlt' ? 'Nur bei bezahlten Investitionen' : 'Ertragberechtigung'}
                                      />
                                    </td>
                                    <td className="p-3">
                                      <div className="flex flex-col gap-1">
                                        {inv.cancellation_status === 'requested' && (
                                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded font-semibold whitespace-nowrap">
                                            Stornierung beantragt
                                          </span>
                                        )}
                                        {(inv.cancellation_status === 'approved' || inv.payment_status === 'storniert') && (
                                          <div className="flex flex-col items-center gap-1">
                                            <input type="checkbox" checked={true} readOnly className="w-4 h-4 text-red-600" />
                                            <span className="text-xs text-red-700 font-semibold whitespace-nowrap">
                                              Storniert
                                            </span>
                                          </div>
                                        )}
                                        {inv.cancellation_status === 'rejected' && (
                                          <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded font-semibold whitespace-nowrap">
                                            Abgelehnt
                                          </span>
                                        )}
                                        {(!inv.cancellation_status || inv.cancellation_status === 'none') && inv.payment_status !== 'storniert' && (
                                          <span className="text-xs text-slate-400">-</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      {isCancelled ? (
                                        <span className="text-xs text-slate-500 italic">Storniert</span>
                                      ) : (
                                        <div className="flex flex-col gap-1">
                                          {/* First row: Bezahlt & Teilweise buttons */}
                                          <div className="flex gap-1">
                                            {inv.payment_status !== 'bezahlt' && (
                                              <Button
                                                size="sm"
                                                onClick={() => handleUpdatePayment(inv.id, 'bezahlt')}
                                                className="bg-green-600 hover:bg-green-700 text-white text-xs whitespace-nowrap"
                                                data-testid={`mark-paid-${index}`}
                                              >
                                                ✓ Bezahlt
                                              </Button>
                                            )}
                                            {(inv.payment_status === 'offen' || inv.payment_status === 'teilweise_bezahlt') && (
                                              <Button
                                                size="sm"
                                                onClick={() => handleUpdatePayment(inv.id, 'teilweise_bezahlt')}
                                                variant="outline"
                                                className="text-yellow-600 border-yellow-600 hover:bg-yellow-50 text-xs whitespace-nowrap"
                                                data-testid={`mark-partial-${index}`}
                                              >
                                                Teilweise
                                              </Button>
                                            )}
                                          </div>
                                          
                                          {/* Second row: Zurücksetzen button */}
                                          {inv.payment_status !== 'offen' && (
                                            <Button
                                              size="sm"
                                              onClick={() => handleUpdatePayment(inv.id, 'offen')}
                                              variant="outline"
                                              className="text-slate-600 text-xs whitespace-nowrap w-full"
                                              data-testid={`mark-open-${index}`}
                                            >
                                              Zurücksetzen
                                            </Button>
                                          )}
                                          
                                          {/* Third row: Stornieren button */}
                                          {inv.payment_status === 'offen' && (
                                            <Button
                                              size="sm"
                                              onClick={() => handleCancelInvestment(inv.id, project?.title || 'Unbekanntes Projekt', inv.shares)}
                                              variant="outline"
                                              className="text-red-600 border-red-600 hover:bg-red-50 text-xs whitespace-nowrap w-full"
                                              data-testid={`cancel-${index}`}
                                            >
                                              🚫 Stornieren
                                            </Button>
                                          )}
                                          {user.role === 'superadmin' && (
                                            <Button
                                              size="sm"
                                              onClick={() => handleDeleteInvestment(inv.id, inv.investment_number, inv.shares)}
                                              variant="outline"
                                              className="text-red-700 border-red-700 hover:bg-red-100 text-xs whitespace-nowrap w-full font-bold"
                                              data-testid={`delete-${index}`}
                                            >
                                              🗑️ Löschen
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      {/* Payment Statistics - Clickable Filters */}
                      <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
                        <button
                          onClick={() => setPaymentStatusFilter(paymentStatusFilter === 'offen' ? null : 'offen')}
                          className={`p-4 border rounded-lg text-left transition-all hover:shadow-lg ${
                            paymentStatusFilter === 'offen'
                              ? 'bg-red-100 border-red-400 ring-2 ring-red-500'
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <p className="text-sm text-red-700 mb-1">Offene Zahlungen</p>
                          <p className="text-2xl font-bold text-red-900">
                            {investments.filter(i => i.payment_status === 'offen' && i.cancellation_status !== 'approved').length}
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            €{investments.filter(i => i.payment_status === 'offen' && i.cancellation_status !== 'approved').reduce((sum, i) => sum + i.total_amount, 0).toLocaleString()}
                          </p>
                          {paymentStatusFilter === 'offen' && (
                            <p className="text-xs text-red-700 font-semibold mt-2">✓ Filter aktiv</p>
                          )}
                        </button>
                        <button
                          onClick={() => setPaymentStatusFilter(paymentStatusFilter === 'teilweise_bezahlt' ? null : 'teilweise_bezahlt')}
                          className={`p-4 border rounded-lg text-left transition-all hover:shadow-lg ${
                            paymentStatusFilter === 'teilweise_bezahlt'
                              ? 'bg-yellow-100 border-yellow-400 ring-2 ring-yellow-500'
                              : 'bg-yellow-50 border-yellow-200'
                          }`}
                        >
                          <p className="text-sm text-yellow-700 mb-1">Teilweise bezahlt</p>
                          <p className="text-2xl font-bold text-yellow-900">
                            {investments.filter(i => i.payment_status === 'teilweise_bezahlt').length}
                          </p>
                          <p className="text-xs text-yellow-600 mt-1">
                            €{investments.filter(i => i.payment_status === 'teilweise_bezahlt').reduce((sum, i) => sum + i.total_amount, 0).toLocaleString()}
                          </p>
                          {paymentStatusFilter === 'teilweise_bezahlt' && (
                            <p className="text-xs text-yellow-700 font-semibold mt-2">✓ Filter aktiv</p>
                          )}
                        </button>
                        <button
                          onClick={() => setPaymentStatusFilter(paymentStatusFilter === 'bezahlt' ? null : 'bezahlt')}
                          className={`p-4 border rounded-lg text-left transition-all hover:shadow-lg ${
                            paymentStatusFilter === 'bezahlt'
                              ? 'bg-green-100 border-green-400 ring-2 ring-green-500'
                              : 'bg-green-50 border-green-200'
                          }`}
                        >
                          <p className="text-sm text-green-700 mb-1">Bezahlt</p>
                          <p className="text-2xl font-bold text-green-900">
                            {investments.filter(i => i.payment_status === 'bezahlt').length}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            €{investments.filter(i => i.payment_status === 'bezahlt').reduce((sum, i) => sum + i.total_amount, 0).toLocaleString()}
                          </p>
                          {paymentStatusFilter === 'bezahlt' && (
                            <p className="text-xs text-green-700 font-semibold mt-2">✓ Filter aktiv</p>
                          )}
                        </button>
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-700 mb-1">Berechtigt</p>
                          <p className="text-2xl font-bold text-blue-900">
                            {investments.filter(i => i.profit_eligible === true && i.cancellation_status !== 'approved').length}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            €{investments.filter(i => i.profit_eligible === true && i.cancellation_status !== 'approved').reduce((sum, i) => sum + i.total_amount, 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-700 mb-1">Storniert</p>
                          <p className="text-2xl font-bold text-gray-900">
                            {investments.filter(i => i.cancellation_status === 'approved' || i.status === 'cancelled').length}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            €{investments.filter(i => i.cancellation_status === 'approved' || i.status === 'cancelled').reduce((sum, i) => sum + i.total_amount, 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                )}

                {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && (
                  <TabsContent value="profit-management">
                    <div className="card">
                      <div className="mb-6 flex justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900 mb-2">Erträge verwalten</h2>
                          <p className="text-sm text-slate-600">
                            Tragen Sie monatliche Projekt-Erträge ein. Diese werden automatisch anteilig auf Investoren verteilt.
                          </p>
                          <p className="text-xs text-orange-600 font-semibold mt-2">
                            ⚠️ Hinweis: Erträge werden nur auf gewinnberechtigte Investitionen verteilt (bezahlt + in Zahlungsverwaltung markiert)
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setShowPrintModal(true)}
                            variant="outline"
                            className="border-green-500 text-green-600 hover:bg-green-50"
                          >
                            🖨️ Drucken
                          </Button>
                          <Button
                            onClick={handleRecalculateProfits}
                            variant="outline"
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            🔄 Neuberechnen
                          </Button>
                          <Button
                            onClick={handleMigrateProfitEligible}
                            variant="outline"
                            className="border-purple-500 text-purple-600 hover:bg-purple-50"
                          >
                            ⚡ Alle Bezahlten markieren
                          </Button>
                        </div>
                      </div>

                      {/* Profit Entry Form */}
                      <form onSubmit={handleCreateProfit} className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mb-8">
                        <h3 className="text-lg font-bold text-slate-900 mb-4">Neuen Ertrag eintragen</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <Label htmlFor="profit-project">Projekt *</Label>
                            <select
                              id="profit-project"
                              value={profitForm.project_id}
                              onChange={(e) => setProfitForm({...profitForm, project_id: e.target.value})}
                              className="w-full px-3 py-2 border-2 border-slate-200 rounded-md focus:outline-none focus:border-green-500"
                              required
                            >
                              <option value="">Projekt auswählen</option>
                              {projects.map((project) => (
                                <option key={project.id} value={project.id}>{project.title}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <Label htmlFor="profit-month">Monat (YYYY-MM) *</Label>
                            <Input
                              id="profit-month"
                              type="month"
                              value={profitForm.month}
                              onChange={(e) => setProfitForm({...profitForm, month: e.target.value})}
                              min="2020-01"
                              max="2099-12"
                              required
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Wählen Sie den Monat, für den der Ertrag ausgeschüttet werden soll
                            </p>
                          </div>

                          <div>
                            <Label htmlFor="profit-amount">Betrag (€) *</Label>
                            <Input
                              id="profit-amount"
                              type="number"
                              step="0.01"
                              value={profitForm.amount}
                              onChange={(e) => setProfitForm({...profitForm, amount: e.target.value})}
                              placeholder="z.B. 50000"
                              required
                            />
                          </div>

                          <div className="flex items-end">
                            <Button
                              type="submit"
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                              + Ertrag eintragen
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-3">
                          💡 Der eingetragene Betrag wird automatisch anteilig auf alle Investoren dieses Projekts verteilt.
                        </p>
                      </form>

                      {/* Existing Profits Overview */}
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 mb-4">Eingetragene Erträge</h3>
                        {allProfits.length === 0 ? (
                          <p className="text-slate-600">Noch keine Erträge eingetragen.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                  <th className="text-left p-3 text-sm font-semibold text-slate-700 w-8"></th>
                                  <th className="text-left p-3 text-sm font-semibold text-slate-700">Projekt</th>
                                  <th className="text-left p-3 text-sm font-semibold text-slate-700">Monat</th>
                                  <th className="text-left p-3 text-sm font-semibold text-slate-700">Betrag</th>
                                  <th className="text-left p-3 text-sm font-semibold text-slate-700">Eingetragen am</th>
                                  <th className="text-center p-3 text-sm font-semibold text-slate-700">Aktion</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allProfits.map((profit, index) => {
                                  const project = projects.find(p => p.id === profit.project_id);
                                  const isExpanded = expandedProfits[profit.id];
                                  const distribution = profitDistributions[profit.id];
                                  const isLoading = loadingDistribution[profit.id];
                                  
                                  return (
                                    <React.Fragment key={profit.id || index}>
                                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3">
                                          <button
                                            onClick={() => toggleProfitDistribution(profit.id)}
                                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded transition-all"
                                            title="Verteilung anzeigen"
                                          >
                                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                          </button>
                                        </td>
                                        <td className="p-3">
                                          <p className="text-sm font-medium text-slate-900">{project?.title || profit.project_id}</p>
                                        </td>
                                        <td className="p-3">
                                          <p className="text-sm text-slate-700">{formatMonthYear(profit.month)}</p>
                                        </td>
                                        <td className="p-3">
                                          <p className="text-sm font-bold text-green-600">€{profit.amount?.toLocaleString()}</p>
                                        </td>
                                        <td className="p-3">
                                          <p className="text-xs text-slate-500">
                                            {profit.created_at ? new Date(profit.created_at).toLocaleDateString('de-DE') : 'N/A'}
                                          </p>
                                        </td>
                                        <td className="p-3 text-center">
                                          <div className="flex gap-2 justify-center">
                                            <button
                                              onClick={() => {
                                                setEditingProfit(profit);
                                                setProfitEditForm({
                                                  amount: profit.amount || '',
                                                  resend_notifications: true
                                                });
                                                setShowProfitEditModal(true);
                                              }}
                                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded transition-all"
                                              title="Ertrag korrigieren"
                                            >
                                              <Edit size={18} />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteProfit(profit.id)}
                                              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded transition-all"
                                              title="Ertrag löschen"
                                            >
                                              <Trash2 size={18} />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                      
                                      {/* Expandable Distribution Section */}
                                      {isExpanded && (
                                        <tr className="bg-blue-50">
                                          <td colSpan="6" className="p-4">
                                            {isLoading ? (
                                              <div className="text-center py-4">
                                                <p className="text-slate-600">Lade Verteilung...</p>
                                              </div>
                                            ) : distribution ? (
                                              <div className="bg-white rounded-lg p-4 border border-blue-200">
                                                <div className="flex justify-between items-center mb-4">
                                                  <h4 className="text-lg font-bold text-slate-900">
                                                    Ertragverteilung - {distribution.project_title}
                                                  </h4>
                                                  <div className="text-sm text-slate-600">
                                                    <span className="font-semibold">{distribution.total_users}</span> Investoren
                                                  </div>
                                                </div>
                                                
                                                {distribution.distribution.length === 0 ? (
                                                  <p className="text-slate-600 text-center py-4">
                                                    Keine bezahlten Investitionen für diesen Ertrag
                                                  </p>
                                                ) : (
                                                  <div className="overflow-x-auto">
                                                    <table className="w-full">
                                                      <thead>
                                                        <tr className="border-b border-slate-200 bg-slate-100">
                                                          <th className="text-left p-2 text-xs font-semibold text-slate-700">Investor</th>
                                                          <th className="text-right p-2 text-xs font-semibold text-slate-700">Gesamtinvestition</th>
                                                          <th className="text-right p-2 text-xs font-semibold text-slate-700">Anteile</th>
                                                          <th className="text-right p-2 text-xs font-semibold text-slate-700">Anteil %</th>
                                                          <th className="text-right p-2 text-xs font-semibold text-slate-700">Ertrag</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {distribution.distribution.map((userDist, idx) => (
                                                          <tr key={`${profit.id}-${idx}`} className="border-b border-slate-100 hover:bg-slate-50">
                                                            <td className="p-2">
                                                              <p className="text-sm font-medium text-slate-900">{userDist.user_name}</p>
                                                              <p className="text-xs text-slate-500">{userDist.user_email}</p>
                                                            </td>
                                                            <td className="p-2 text-right">
                                                              <p className="text-sm text-slate-700">€{userDist.investment_amount?.toLocaleString()}</p>
                                                            </td>
                                                            <td className="p-2 text-right">
                                                              <p className="text-sm text-slate-700">{userDist.shares}</p>
                                                            </td>
                                                            <td className="p-2 text-right">
                                                              <p className="text-sm text-slate-700">{userDist.share_percentage}%</p>
                                                            </td>
                                                            <td className="p-2 text-right">
                                                              <p className="text-sm font-bold text-green-600">€{userDist.profit_amount?.toLocaleString()}</p>
                                                            </td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                      <tfoot>
                                                        <tr className="border-t-2 border-slate-300 bg-slate-50">
                                                          <td className="p-2 font-semibold text-slate-900">
                                                            Summe ({distribution.total_users} Investoren)
                                                          </td>
                                                          <td className="p-2 text-right">
                                                            <p className="text-sm font-bold text-slate-900">
                                                              €{distribution.distribution.reduce((sum, d) => sum + (d.investment_amount || 0), 0).toLocaleString()}
                                                            </p>
                                                          </td>
                                                          <td className="p-2 text-right">
                                                            <p className="text-sm font-bold text-slate-900">
                                                              {distribution.distribution.reduce((sum, d) => sum + (d.shares || 0), 0)}
                                                            </p>
                                                          </td>
                                                          <td className="p-2 text-right">
                                                            <p className="text-sm font-bold text-slate-900">
                                                              {distribution.distribution.reduce((sum, d) => sum + (parseFloat(d.share_percentage) || 0), 0).toFixed(2)}%
                                                            </p>
                                                          </td>
                                                          <td className="p-2 text-right">
                                                            <p className="text-sm font-bold text-green-700">
                                                              €{distribution.distribution.reduce((sum, d) => sum + (d.profit_amount || 0), 0).toLocaleString()}
                                                            </p>
                                                          </td>
                                                        </tr>
                                                      </tfoot>
                                                    </table>
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <div className="text-center py-4">
                                                <p className="text-red-600">Fehler beim Laden der Verteilung</p>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                )}

                {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && (
                  <TabsContent value="bank-accounts">
                    <div className="card">
                      <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                          <h2 className="text-2xl font-bold text-slate-900">Bankdaten-Anfragen</h2>
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-slate-600">Ansicht:</label>
                            <select
                              value={bankRequestsSortBy}
                              onChange={(e) => setBankRequestsSortBy(e.target.value)}
                              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="all">Gesamtliste</option>
                              <option value="user">Nach User</option>
                              <option value="project">Nach Projekt</option>
                            </select>
                          </div>
                        </div>
                        <p className="text-slate-600">Überprüfen Sie die von Investoren übermittelten Bankverbindungen für Auszahlungen</p>
                      </div>

                      {(() => {
                        // Get all investments with bank details
                        const investmentsWithBankDetails = investments.filter(inv => inv.bank_details?.iban);
                        
                        if (investmentsWithBankDetails.length === 0) {
                          return (
                            <div className="text-center py-12">
                              <p className="text-slate-600">Keine Bankdaten-Anfragen vorhanden</p>
                            </div>
                          );
                        }
                        
                        // Show complete list without grouping
                        if (bankRequestsSortBy === 'all') {
                          return (
                            <div className="space-y-4">
                              {investmentsWithBankDetails.map((inv) => {
                                const project = projects.find(p => p.id === inv.project_id);
                                const investor = users.find(u => u.id === inv.user_id);
                                const isReviewed = inv.bank_details?.reviewed || false;
                                
                                return (
                                  <div key={inv.id} className={`border rounded-lg overflow-hidden ${isReviewed ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                                    <div className="p-4">
                                      <div className="flex justify-between items-start mb-4">
                                        <div>
                                          <div className="flex items-center gap-2 mb-2">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${isReviewed ? 'bg-green-600 text-white' : 'bg-orange-600 text-white'}`}>
                                              {isReviewed ? '✓ Bearbeitet' : '⏳ Wartet auf Prüfung'}
                                            </span>
                                          </div>
                                          <h3 className="text-lg font-bold text-slate-900">{investor?.username || 'Unbekannt'}</h3>
                                          <p className="text-sm text-slate-600">{investor?.email || ''}</p>
                                          <p className="text-sm text-slate-600 mt-1">
                                            <span className="font-medium">Projekt:</span> {project?.title || inv.project_id}
                                          </p>
                                          <p className="text-sm text-slate-600">
                                            <span className="font-medium">Investition:</span> {inv.investment_number} • {inv.shares} Anteile
                                          </p>
                                          {inv.bank_details?.submitted_at && (
                                            <p className="text-xs text-slate-500 mt-2">
                                              📅 Eingegangen: {new Date(inv.bank_details.submitted_at).toLocaleString('de-DE', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </p>
                                          )}
                                          {isReviewed && inv.bank_details?.reviewed_at && (
                                            <p className="text-xs text-green-700 mt-1">
                                              ✓ Bearbeitet: {new Date(inv.bank_details.reviewed_at).toLocaleString('de-DE', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </p>
                                          )}
                                        </div>
                                      </div>

                                      <div className="bg-white rounded-lg p-4 mb-4">
                                        <h4 className="font-semibold text-slate-900 mb-3">Bankverbindung:</h4>
                                        <div className="space-y-2">
                                          <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">Kontoinhaber:</span>
                                            <span className="font-medium text-slate-900">{inv.bank_details.account_holder}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">IBAN:</span>
                                            <span className="font-mono font-medium text-slate-900 text-sm">{inv.bank_details.iban}</span>
                                          </div>
                                          {inv.bank_details.bic && (
                                            <div className="flex justify-between">
                                              <span className="text-sm text-slate-500">BIC:</span>
                                              <span className="font-medium text-slate-900">{inv.bank_details.bic}</span>
                                            </div>
                                          )}
                                          {inv.bank_details.bank_name && (
                                            <div className="flex justify-between">
                                              <span className="text-sm text-slate-500">Bank:</span>
                                              <span className="font-medium text-slate-900">{inv.bank_details.bank_name}</span>
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {!isReviewed && (
                                        <div className="flex gap-2 justify-end">
                                          <Button
                                            onClick={async () => {
                                              try {
                                                const token = localStorage.getItem('token');
                                                await axios.put(`${API}/investments/${inv.id}/bank-details`, 
                                                  {
                                                    ...inv.bank_details,
                                                    reviewed: true,
                                                    reviewed_at: new Date().toISOString()
                                                  },
                                                  { headers: { Authorization: `Bearer ${token}` } }
                                                );
                                                toast.success('Als bearbeitet markiert');
                                                fetchData();
                                              } catch (error) {
                                                toast.error('Fehler beim Markieren');
                                              }
                                            }}
                                            className="bg-green-600 hover:bg-green-700"
                                          >
                                            ✓ Als bearbeitet markieren
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }
                        
                        // Group by user or project based on sort preference
                        let grouped = {};
                        
                        if (bankRequestsSortBy === 'user') {
                          // Group by user
                          investmentsWithBankDetails.forEach(inv => {
                            if (!grouped[inv.user_id]) {
                              grouped[inv.user_id] = {
                                user: users.find(u => u.id === inv.user_id),
                                investments: []
                              };
                            }
                            grouped[inv.user_id].investments.push(inv);
                          });
                        } else {
                          // Group by project
                          investmentsWithBankDetails.forEach(inv => {
                            if (!grouped[inv.project_id]) {
                              grouped[inv.project_id] = {
                                project: projects.find(p => p.id === inv.project_id),
                                investments: []
                              };
                            }
                            grouped[inv.project_id].investments.push(inv);
                          });
                        }
                        
                        return (
                          <div className="space-y-4">
                            {Object.entries(grouped).map(([key, data]) => {
                              const isExpanded = expandedBankRequests[key] || false;
                              
                              if (bankRequestsSortBy === 'user') {
                                const user = data.user;
                                const unreviewed = data.investments.filter(inv => !inv.bank_details?.reviewed).length;
                                
                                return (
                                  <div key={key} className="border border-slate-200 rounded-lg overflow-hidden">
                                    <div 
                                      className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 cursor-pointer hover:from-blue-100 hover:to-purple-100 transition-colors"
                                      onClick={() => setExpandedBankRequests(prev => ({ ...prev, [key]: !prev[key] }))}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                                            {user?.username?.charAt(0).toUpperCase() || '?'}
                                          </div>
                                          <div>
                                            <h3 className="text-lg font-bold text-slate-900">{user?.username || 'Unbekannt'}</h3>
                                            <p className="text-sm text-slate-600">{user?.email || ''}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                              <span className="text-xs text-slate-500">{data.investments.length} Investition(en)</span>
                                              {unreviewed > 0 && (
                                                <span className="px-2 py-0.5 bg-orange-600 text-white rounded-full text-xs font-semibold">
                                                  {unreviewed} offen
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <span className="text-slate-400 text-2xl">{isExpanded ? '▼' : '▶'}</span>
                                      </div>
                                    </div>
                                    
                                    {isExpanded && (
                                      <div className="p-4 space-y-4 bg-slate-50">
                                        {data.investments.map((inv) => {
                                          const project = projects.find(p => p.id === inv.project_id);
                                          const isReviewed = inv.bank_details?.reviewed || false;
                                          
                                          return (
                                            <div key={inv.id} className={`border rounded-lg p-4 ${isReviewed ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                                              <div className="flex justify-between items-start mb-3">
                                                <div>
                                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${isReviewed ? 'bg-green-600 text-white' : 'bg-orange-600 text-white'}`}>
                                                    {isReviewed ? '✓ Bearbeitet' : '⏳ Wartet auf Prüfung'}
                                                  </span>
                                                  <p className="text-sm font-semibold text-slate-900 mt-2">
                                                    {project?.title || inv.project_id}
                                                  </p>
                                                  <p className="text-xs text-slate-600">
                                                    {inv.investment_number} • {inv.shares} Anteile
                                                  </p>
                                                  {inv.bank_details?.submitted_at && (
                                                    <p className="text-xs text-slate-500 mt-1">
                                                      📅 Eingegangen: {new Date(inv.bank_details.submitted_at).toLocaleDateString('de-DE')}
                                                    </p>
                                                  )}
                                                  {isReviewed && inv.bank_details?.reviewed_at && (
                                                    <p className="text-xs text-green-700 mt-1">
                                                      ✓ Bearbeitet: {new Date(inv.bank_details.reviewed_at).toLocaleDateString('de-DE')}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>

                                              <div className="bg-white rounded-lg p-3">
                                                <div className="space-y-1 text-sm">
                                                  <div className="flex justify-between">
                                                    <span className="text-slate-500">Kontoinhaber:</span>
                                                    <span className="font-medium">{inv.bank_details.account_holder}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-slate-500">IBAN:</span>
                                                    <span className="font-mono text-xs">{inv.bank_details.iban}</span>
                                                  </div>
                                                  {inv.bank_details.bic && (
                                                    <div className="flex justify-between">
                                                      <span className="text-slate-500">BIC:</span>
                                                      <span className="font-medium">{inv.bank_details.bic}</span>
                                                    </div>
                                                  )}
                                                  {inv.bank_details.bank_name && (
                                                    <div className="flex justify-between">
                                                      <span className="text-slate-500">Bank:</span>
                                                      <span className="font-medium">{inv.bank_details.bank_name}</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>

                                              {!isReviewed && (
                                                <div className="mt-3 flex justify-end">
                                                  <Button
                                                    size="sm"
                                                    onClick={async () => {
                                                      try {
                                                        const token = localStorage.getItem('token');
                                                        await axios.put(`${API}/investments/${inv.id}/bank-details`, 
                                                          {
                                                            ...inv.bank_details,
                                                            reviewed: true,
                                                            reviewed_at: new Date().toISOString()
                                                          },
                                                          { headers: { Authorization: `Bearer ${token}` } }
                                                        );
                                                        toast.success('Als bearbeitet markiert');
                                                        fetchData();
                                                      } catch (error) {
                                                        toast.error('Fehler beim Markieren');
                                                      }
                                                    }}
                                                    className="bg-green-600 hover:bg-green-700"
                                                  >
                                                    ✓ Als bearbeitet markieren
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              } else {
                                // Sort by project
                                const project = data.project;
                                const unreviewed = data.investments.filter(inv => !inv.bank_details?.reviewed).length;
                                
                                return (
                                  <div key={key} className="border border-slate-200 rounded-lg overflow-hidden">
                                    <div 
                                      className="bg-gradient-to-r from-green-50 to-blue-50 p-4 cursor-pointer hover:from-green-100 hover:to-blue-100 transition-colors"
                                      onClick={() => setExpandedBankRequests(prev => ({ ...prev, [key]: !prev[key] }))}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          {project?.image_url && (
                                            <img 
                                              src={project.image_url} 
                                              alt={project.title}
                                              className="w-12 h-12 rounded-lg object-cover"
                                            />
                                          )}
                                          <div>
                                            <h3 className="text-lg font-bold text-slate-900">{project?.title || 'Unbekanntes Projekt'}</h3>
                                            <div className="flex items-center gap-2 mt-1">
                                              <span className="text-xs text-slate-500">{data.investments.length} Investition(en)</span>
                                              {unreviewed > 0 && (
                                                <span className="px-2 py-0.5 bg-orange-600 text-white rounded-full text-xs font-semibold">
                                                  {unreviewed} offen
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <span className="text-slate-400 text-2xl">{isExpanded ? '▼' : '▶'}</span>
                                      </div>
                                    </div>
                                    
                                    {isExpanded && (
                                      <div className="p-4 space-y-4 bg-slate-50">
                                        {data.investments.map((inv) => {
                                          const investor = users.find(u => u.id === inv.user_id);
                                          const isReviewed = inv.bank_details?.reviewed || false;
                                          
                                          return (
                                            <div key={inv.id} className={`border rounded-lg p-4 ${isReviewed ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                                              <div className="flex justify-between items-start mb-3">
                                                <div>
                                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${isReviewed ? 'bg-green-600 text-white' : 'bg-orange-600 text-white'}`}>
                                                    {isReviewed ? '✓ Bearbeitet' : '⏳ Wartet auf Prüfung'}
                                                  </span>
                                                  <p className="text-sm font-semibold text-slate-900 mt-2">
                                                    {investor?.username || 'Unbekannt'}
                                                  </p>
                                                  <p className="text-xs text-slate-600">
                                                    {investor?.email || ''} • {inv.investment_number} • {inv.shares} Anteile
                                                  </p>
                                                  {inv.bank_details?.submitted_at && (
                                                    <p className="text-xs text-slate-500 mt-1">
                                                      📅 Eingegangen: {new Date(inv.bank_details.submitted_at).toLocaleDateString('de-DE')}
                                                    </p>
                                                  )}
                                                  {isReviewed && inv.bank_details?.reviewed_at && (
                                                    <p className="text-xs text-green-700 mt-1">
                                                      ✓ Bearbeitet: {new Date(inv.bank_details.reviewed_at).toLocaleDateString('de-DE')}
                                                    </p>
                                                  )}
                                                </div>
                                              </div>

                                              <div className="bg-white rounded-lg p-3">
                                                <div className="space-y-1 text-sm">
                                                  <div className="flex justify-between">
                                                    <span className="text-slate-500">Kontoinhaber:</span>
                                                    <span className="font-medium">{inv.bank_details.account_holder}</span>
                                                  </div>
                                                  <div className="flex justify-between">
                                                    <span className="text-slate-500">IBAN:</span>
                                                    <span className="font-mono text-xs">{inv.bank_details.iban}</span>
                                                  </div>
                                                  {inv.bank_details.bic && (
                                                    <div className="flex justify-between">
                                                      <span className="text-slate-500">BIC:</span>
                                                      <span className="font-medium">{inv.bank_details.bic}</span>
                                                    </div>
                                                  )}
                                                  {inv.bank_details.bank_name && (
                                                    <div className="flex justify-between">
                                                      <span className="text-slate-500">Bank:</span>
                                                      <span className="font-medium">{inv.bank_details.bank_name}</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>

                                              {!isReviewed && (
                                                <div className="mt-3 flex justify-end">
                                                  <Button
                                                    size="sm"
                                                    onClick={async () => {
                                                      try {
                                                        const token = localStorage.getItem('token');
                                                        await axios.put(`${API}/investments/${inv.id}/bank-details`, 
                                                          {
                                                            ...inv.bank_details,
                                                            reviewed: true,
                                                            reviewed_at: new Date().toISOString()
                                                          },
                                                          { headers: { Authorization: `Bearer ${token}` } }
                                                        );
                                                        toast.success('Als bearbeitet markiert');
                                                        fetchData();
                                                      } catch (error) {
                                                        toast.error('Fehler beim Markieren');
                                                      }
                                                    }}
                                                    className="bg-green-600 hover:bg-green-700"
                                                  >
                                                    ✓ Als bearbeitet markieren
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  </TabsContent>
                )}

                {(user.role === 'admin' || user.role === 'finance' || user.role === 'superadmin') && (
                  <TabsContent value="cancellations">
                    <div className="card">
                      <div className="mb-6">
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">Stornierungsanfragen</h2>
                        <p className="text-slate-600">Verwalten Sie Anfragen zur Investitionsstornierung</p>
                      </div>

                      {investments.filter(inv => inv.cancellation_status === 'requested').length === 0 ? (
                        <div className="text-center py-12">
                          <p className="text-slate-600">Keine offenen Stornierungsanfragen</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {investments.filter(inv => inv.cancellation_status === 'requested').map((inv, index) => {
                            const project = projects.find(p => p.id === inv.project_id);
                            const investor = users.find(u => u.id === inv.user_id);
                            
                            return (
                              <div key={inv.id} className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                      <h3 className="font-bold text-slate-900">{inv.investment_number}</h3>
                                      <span className="text-xs bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-semibold">
                                        Stornierung beantragt
                                      </span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 mb-3">
                                      <div>
                                        <p className="text-xs text-slate-600">Projekt</p>
                                        <p className="text-sm font-semibold text-slate-900">{project?.title || 'Unbekannt'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-600">Investor</p>
                                        <p className="text-sm font-semibold text-slate-900">{investor?.username || 'Unbekannt'}</p>
                                        <p className="text-xs text-slate-500">{investor?.email}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-600">Anteile</p>
                                        <p className="text-sm font-semibold text-slate-900">{inv.shares} Anteile</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-600">Investitionssumme</p>
                                        <p className="text-sm font-semibold text-green-600">€{inv.total_amount?.toLocaleString()}</p>
                                      </div>
                                    </div>

                                    <div className="bg-white p-3 rounded border border-orange-200 mb-3">
                                      <p className="text-xs text-slate-600 mb-1">Grund für Stornierung:</p>
                                      <p className="text-sm text-slate-900 italic">{inv.cancellation_reason || 'Kein Grund angegeben'}</p>
                                    </div>

                                    <p className="text-xs text-slate-500">
                                      Beantragt am: {new Date(inv.cancellation_requested_at).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  </div>

                                  <div className="flex flex-col gap-2 ml-4">
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-white"
                                      onClick={() => handleCancellationDecision(inv.id, 'approved')}
                                    >
                                      Genehmigen
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 hover:bg-red-50"
                                      onClick={() => handleCancellationDecision(inv.id, 'rejected')}
                                    >
                                      Ablehnen
                                    </Button>
                                    {user.role === 'superadmin' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-red-700 border-red-700 hover:bg-red-100 font-bold"
                                        onClick={() => handleDeleteInvestment(inv.id, inv.investment_number, inv.shares)}
                                      >
                                        🗑️ Löschen
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-slate-900">Bericht exportieren</h2>
              <button onClick={() => setShowExportModal(false)} className="text-slate-500 hover:text-slate-700">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Export Type */}
              <div>
                <Label>Export-Typ</Label>
                <select
                  value={exportConfig.type}
                  onChange={(e) => setExportConfig({...exportConfig, type: e.target.value, entity_id: ''})}
                  className="w-full border rounded p-2"
                >
                  <option value="user">Nach User</option>
                  <option value="project">Nach Projekt</option>
                </select>
              </div>

              {/* Entity Selection */}
              <div>
                <Label>{exportConfig.type === 'user' ? 'User auswählen' : 'Projekt auswählen'}</Label>
                <select
                  value={exportConfig.entity_id}
                  onChange={(e) => setExportConfig({...exportConfig, entity_id: e.target.value})}
                  className="w-full border rounded p-2"
                >
                  <option value="">-- Bitte wählen --</option>
                  {exportConfig.type === 'user' 
                    ? users.filter(u => u.role === 'user').map(u => (
                        <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
                      ))
                    : projects.map(p => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))
                  }
                </select>
              </div>

              {/* Format is always PDF now */}
              <div>
                <Label>Format</Label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="pdf"
                      checked={true}
                      disabled
                      className="mr-2"
                    />
                    PDF
                  </label>
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Von (optional)</Label>
                  <Input
                    type="date"
                    value={exportConfig.start_date}
                    onChange={(e) => setExportConfig({...exportConfig, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Bis (optional)</Label>
                  <Input
                    type="date"
                    value={exportConfig.end_date}
                    onChange={(e) => setExportConfig({...exportConfig, end_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowExportModal(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleExportReport} className="bg-purple-600 hover:bg-purple-700">
                  <FileDown size={16} className="mr-2" />
                  Exportieren
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profit Edit Modal */}
      {showProfitEditModal && editingProfit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Ertrag korrigieren</h3>
            
            <div className="mb-4">
              <p className="text-sm text-slate-600 mb-2">
                <strong>Projekt:</strong> {projects.find(p => p.id === editingProfit.project_id)?.title}
              </p>
              <p className="text-sm text-slate-600 mb-4">
                <strong>Monat:</strong> {editingProfit.month}
              </p>
              
              <Label>Neuer Betrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={profitEditForm.amount}
                onChange={(e) => setProfitEditForm({...profitEditForm, amount: e.target.value})}
                className="mb-4"
              />
              
              <div className="flex items-start gap-2 mb-4">
                <input
                  type="checkbox"
                  id="resend_notifications"
                  checked={profitEditForm.resend_notifications}
                  onChange={(e) => setProfitEditForm({...profitEditForm, resend_notifications: e.target.checked})}
                  className="mt-1"
                />
                <div>
                  <label htmlFor="resend_notifications" className="text-sm font-semibold cursor-pointer">
                    Benachrichtigungen an neue Investoren senden
                  </label>
                  <p className="text-xs text-slate-500">
                    Nur Investoren, die noch keine E-Mail für diesen Ertrag erhalten haben und zum Zeitpunkt des Ertrags bereits investiert hatten, werden benachrichtigt.
                  </p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Hinweis:</strong> Der Ertragbetrag wird aktualisiert. Bereits benachrichtigte Investoren erhalten keine erneute E-Mail.
                </p>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowProfitEditModal(false);
                  setEditingProfit(null);
                }}
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleUpdateProfit}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Ertrag aktualisieren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Partial Payment Dialog */}
      {showPartialPaymentDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Neue Teilzahlung erfassen</h3>
            <div className="space-y-4">
              <div>
                <Label>Gesamtbetrag</Label>
                <p className="text-lg font-bold text-slate-900">€{partialPaymentData.totalAmount.toLocaleString()}</p>
              </div>
              <div>
                <Label>Bereits bezahlt</Label>
                <p className="text-base font-semibold text-green-600">€{partialPaymentData.currentPaidAmount.toLocaleString()}</p>
              </div>
              <div>
                <Label htmlFor="newPaymentAmount">Neuer Zahlungsbetrag (€)</Label>
                <Input
                  id="newPaymentAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={partialPaymentData.newPaymentAmount}
                  onChange={(e) => setPartialPaymentData({
                    ...partialPaymentData,
                    newPaymentAmount: e.target.value
                  })}
                  placeholder="Betrag eingeben..."
                  autoFocus
                />
                {partialPaymentData.newPaymentAmount > 0 && (
                  <div className="text-sm mt-2 space-y-1">
                    <p className="text-slate-600">
                      Gesamt nach Zahlung: €{(partialPaymentData.currentPaidAmount + parseFloat(partialPaymentData.newPaymentAmount || 0)).toLocaleString()}
                    </p>
                    <p className="text-green-600 font-semibold">
                      = {(((partialPaymentData.currentPaidAmount + parseFloat(partialPaymentData.newPaymentAmount || 0)) / partialPaymentData.totalAmount) * 100).toFixed(1)}% bezahlt
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowPartialPaymentDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handlePartialPaymentSubmit}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
                disabled={!partialPaymentData.newPaymentAmount || partialPaymentData.newPaymentAmount <= 0}
              >
                Speichern
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Print Profits Modal */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Ertrags-Auszahlungen drucken</h3>
              
              <div className="space-y-4">
                {/* Date Range */}
                <div>
                  <Label>Datumsbereich (Monat)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <div>
                      <label className="text-xs text-slate-500">Von:</label>
                      <Input
                        type="month"
                        value={printFilters.startDate}
                        onChange={(e) => setPrintFilters({...printFilters, startDate: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Bis:</label>
                      <Input
                        type="month"
                        value={printFilters.endDate}
                        onChange={(e) => setPrintFilters({...printFilters, endDate: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Filter Type */}
                <div>
                  <Label>Filtern nach</Label>
                  <select
                    value={printFilters.filterBy}
                    onChange={(e) => setPrintFilters({...printFilters, filterBy: e.target.value, selectedId: ''})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Alle</option>
                    <option value="user">Nach User</option>
                    <option value="project">Nach Projekt</option>
                  </select>
                </div>

                {/* User/Project Selection */}
                {printFilters.filterBy === 'user' && (
                  <div>
                    <Label>User auswählen</Label>
                    <select
                      value={printFilters.selectedId}
                      onChange={(e) => setPrintFilters({...printFilters, selectedId: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Alle User</option>
                      {users.filter(u => u.role === 'user').map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {printFilters.filterBy === 'project' && (
                  <div>
                    <Label>Projekt auswählen</Label>
                    <select
                      value={printFilters.selectedId}
                      onChange={(e) => setPrintFilters({...printFilters, selectedId: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Alle Projekte</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-2 mt-6">
                <Button
                  onClick={() => setShowPrintModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handlePrintProfits}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  🖨️ Drucken
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
